/**
 * The collaborative grid — a custom DOM table on the board canvas
 * (F-10 rewritten 2026-07-10; renderer re-decided in docs/04 §5:
 * custom DOM table, CSS-transform zoom).
 *
 *  - F-10: the table NEVER scrolls internally — it occupies its full size at
 *    GRID_ORIGIN inside the transformed board layer, in BOARD units; the
 *    camera's CSS `translate(...) scale(zoom)` does all zooming (the browser
 *    remaps pointer coordinates under the transform natively). No header row,
 *    no row-number gutter. Rows/columns sorted by (ord, id) — docs/07 C1.
 *  - F-10 VIEWPORT WINDOWING (2026-07-16 amendment, decided in docs/10):
 *    only the rows intersecting the camera view (+ ~1 viewport of buffer) are
 *    MOUNTED. Still no scrollbars — you pan the board as always and rows
 *    mount/unmount as they enter/leave. Three things make that safe:
 *      · Row heights come from `core/grid-sizing.RowHeightCache` (HYBRID:
 *        measured-on-mount is truth, `core/measure.ts` predicts the rest), so
 *        EVERY row has a height with no DOM node. They are applied as the
 *        cell's `min-height`, never `height` — F-26 never crops and a wrong
 *        prediction self-heals when the row first mounts.
 *      · `onLayout` publishes edges for ALL rows, not just mounted ones, and
 *        is no longer DOM-derived. This is load-bearing: docs/07 C4 degrades a
 *        comment/element whose id is missing from a PRESENT layout, so
 *        publishing only the mounted rows would orphan every off-screen pin.
 *      · The mounted block is absolutely positioned at its predicted edge and
 *        the wrapper keeps the table's full extent, so the camera (docs/04 §5)
 *        is untouched and pans over the whole table.
 *    Windowing is NOT for pan (already at the compositor baseline at every
 *    size — one transform on a promoted layer): it is for EDIT LATENCY
 *    (73 ms → 0.6 ms per cell at 10k rows), ZOOM (60 → 12 ms/frame) and MOUNT
 *    (680 → 83 ms). Selection, presence, copy/paste and CSV export span
 *    unrendered rows unchanged — they are id/index-based, never DOM-based.
 *  - F-26: cells wrap (pre-wrap + overflow-wrap:anywhere) and rows auto-grow
 *    via native CSS; a run-width pass (core/table.ts) widens columns for
 *    unwrappable runs up to the 480 board-px cap. Auto sizes are DERIVED —
 *    only manual resizes (F-14) write to the doc, and they act as floors.
 *  - F-12: double-click/Enter opens a textarea overlay sized to the cell that
 *    grows while typing and never clips; Esc cancels, Enter commits
 *    (Shift+Enter newline), Tab/arrows navigate & commit; typing on a
 *    selected cell starts editing replacing content (Sheets muscle memory).
 *  - F-12b: hover handles replace headers — a pill above a hovered column
 *    (click = select, drag = reorder via a single moveColumn ord write,
 *    ⋮/right-click = menu) and the same on a row's left edge.
 *  - F-13: hover "+" bubbles on boundaries in the handle gutters, persistent
 *    "+" strips on the right/bottom edges. F-14: boundary drag-resize with
 *    live local preview and ONE CRDT commit on release (zero-movement clicks
 *    commit nothing — the effective size is derived state, docs/04 §5);
 *    double-click a boundary = autofit to content; a full-column/row
 *    multi-selection resizes/autofits all selected tracks together.
 *  - F-30: cell image values render as <img> thumbnails (≤160 board px tall,
 *    aspect kept); `hitTestCell` resolves a pointer to the cell under it via
 *    board-space rect math for the image-drop target.
 *  - Unchanged from the canvas-grid era: F-15/16 selection & TSV copy/paste
 *    (paste grows the table instead of truncating), F-41 presence highlights,
 *    F-42 soft lock with "Edit anyway" (also guarding bulk writes: clear,
 *    paste), docs/07 C4 stable-id commit guards. F-43 undo shortcuts live on
 *    the canvas shell (BoardCanvas) — session-scoped, not sheet-scoped.
 */
import {
  cellKey,
  colElementWidth,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
  GRID_ORIGIN,
  IMAGE_CELL_TEXT_PLACEHOLDER,
  cellPlainText,
  isBgmCellValue,
  isImageCellValue,
  parseCellKey,
  rowElementHeight,
  segmentMarkedText,
  type CellStatus,
  type CellTextMarks,
  type ColumnInfo,
  type TextTypeSwitch,
} from "@kola/shared";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useStore } from "zustand";

import {
  COARSE_POINTER,
  DOUBLE_TAP_MS,
  LONG_PRESS_MS,
  safeSetPointerCapture,
  TOUCH_SLOP_SCREEN_PX,
  type CanvasStore,
} from "../../core/canvas.js";
import { readableTextColor, themedFill, withAlpha } from "../../core/color.js";
import {
  ColumnRunTracker,
  createRunWidthCache,
  heightSignature,
  RowHeightCache,
} from "../../core/grid-sizing.js";
import { isImeComposing, isImeEnterEcho } from "../../core/keyboard.js";
import { boardTextWidth, FONT_VERSION } from "../../core/measure.js";
import {
  bufferedRowWindow,
  isEmptyRowWindow,
  rowWindowsEqual,
  type RowWindow,
} from "../../core/row-window.js";
import type { BoardSession, RemotePeer } from "../../core/session.js";
import {
  autoColumnWidth,
  autofitColumnWidth,
  CELL_H_PAD,
  CELL_V_PAD,
  cellAtPosition,
  clampManualColWidth,
  clampManualRowHeight,
  edgesFromSizes,
  indexAtPosition,
  indexById,
  maxLineWidth,
  nearestBoundary,
  normalizeRange,
  reorderTarget,
  stepIndex,
  TABLE_BORDER,
  TABLE_FONT_FAMILY,
  TABLE_FONT_PX,
  TABLE_LINE_HEIGHT,
} from "../../core/table.js";
import { useThemeColors } from "../../core/useThemeColors.js";
import {
  requestAddComment,
  type CellsToolbarSelection,
} from "../../registries/toolbar-actions/index.js";
import { isMarquee } from "../elements/marquee.js";
import { IMPORT_CHUNK_ROWS, planChunks } from "../import-export/chunk-plan.js";
import { yieldToUi } from "../import-export/import-script.js";
import { importBusy, type ImportUiStore } from "../import-export/store.js";
import { matrixToTsv, parseTsv } from "../import-export/tsv.js";
import { markClassName, MarkedText, markStyle } from "../../ui/MarkedText.js";
import {
  applyTextTypePick,
  matchTextTypes,
  textTypeOptions,
  slashQueryAt,
  textTypeSwatch,
  type TextTypeOption,
} from "../../ui/text-type.js";
import { useTextKinds } from "../text-kinds/store.js";
import { PlaceVideoButton } from "../place-video/PlaceVideoButton.js";
import { isPlaceVideoCell } from "../place-video/script-header.js";
import { cellShareLink, copyToClipboard } from "../../core/share-link.js";
import { Modal } from "../../ui/Modal.js";
import { TextFormatControls } from "../../ui/TextFormatControls.js";
import { formatShortcut, orderedRange, readFormatState } from "../../ui/text-format.js";
import { showToast } from "../../ui/toast.js";
import type { CopiedCell } from "../../core/cell-copy.js";
import {
  buildCellClipboard,
  CELL_CLIPBOARD_MIME,
  type CellClipboard,
  copiedElementDuplicates,
  parseCellClipboard,
  serializeCellClipboard,
} from "./cell-clipboard.js";
import { canInsertColumnBefore, clampReorderBoundary, columnReorderBounds } from "./column-lock.js";
import { ContextMenu, type MenuItem, type MenuState } from "./ContextMenu.js";

/** Shorten a value for the C4 "row was deleted" notice. */
function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/** F-42 bulk-write guard: message for cells skipped because a peer is editing
 * them (paste/clear get the same block+override treatment as the editor). */
function skippedClaimMessage(count: number, peerName: string | undefined): string {
  return count === 1
    ? `${peerName ?? "Someone"} is editing that cell — it was skipped`
    : `Skipped ${count} cells that others are editing`;
}

/** One cell of a paste block — `item` is whatever the block's flavor carries. */
interface PasteWriteItem<T> {
  rowId: string;
  colId: string;
  item: T;
}

/** Where a pasted block landed — handed to `PastePlan.afterCells`. */
interface PasteTarget {
  /** The rows / columns the block now covers, in block order. */
  rowIds: readonly string[];
  colIds: readonly string[];
  /** Grid index of `rowIds[0]` / `colIds[0]` at paste time. */
  startRow: number;
  startCol: number;
  /** Cells (`cellKey`) left alone because a peer is editing them (F-42). */
  skipped: ReadonlySet<string>;
}

/**
 * A block to land on the grid (F-16) — the two clipboard flavors differ only
 * in what a cell IS, so each supplies the matrix and the writer and shares
 * `applyPaste` for everything else (growth, chunking, claims, one undo step).
 */
interface PastePlan<T> {
  /** Rows of the block; a short row leaves the cells past its end untouched. */
  matrix: readonly (readonly T[])[];
  /** Write one batch of cells as ONE transaction (also the "Paste anyway" path). */
  write: (writes: readonly PasteWriteItem<T>[]) => void;
  /** Runs once, after every cell is written, inside the same undo step. */
  afterCells?: (target: PasteTarget) => void;
}

/** Handle-gutter depth and boundary tolerance in SCREEN px (÷ zoom to board). */
/**
 * Exported so anything placed OUTSIDE the table can prove it clears this strip
 * rather than hard-coding 26 and silently drifting — the design band above the
 * table does exactly that (`features/design/layout.ts`). In BOARD units the
 * strip is `GUTTER_SCREEN_PX / zoom` tall, so it is widest at minimum zoom.
 */
export const GUTTER_SCREEN_PX = 26;
const BOUNDARY_TOLERANCE_SCREEN_PX = 6;
/** Pointer travel (screen px) before a pill press becomes a reorder drag. */
const DRAG_THRESHOLD_SCREEN_PX = 4;
/** F-14 resize strip thickness (screen px) — wider where fingers do the work. */
const RESIZE_STRIP_SCREEN_PX = COARSE_POINTER ? 16 : 6;
/**
 * F-17-marks format bar (screen px, because it counter-scales like every other
 * overlay chrome): its height, used only to decide whether it fits ABOVE the
 * editor, and the gap it keeps from the editor's edge. An estimate is enough —
 * being a few pixels out moves the flip point by one hair, and the bar itself
 * is positioned by its own bottom edge either way.
 */
const FMT_BAR_H = 34;
const FMT_BAR_GAP = 6;

/** A grid cell hit by a pointer position (client coords) — F-30 drop target. */
export interface CellHit {
  rowId: string;
  colId: string;
  /** The cell's rectangle in client (viewport) coordinates. */
  rect: { left: number; top: number; width: number; height: number };
}

/**
 * A highlighted cell the canvas shell can target, with its board-space
 * top-left — everything a paste of copied ITEMS needs to land in it (F-34).
 */
export interface SelectedCell {
  rowId: string;
  colId: string;
  /** The cell's top-left in absolute board units. */
  x: number;
  y: number;
}

/** Imperative surface the canvas shell needs from the grid (F-30). */
export interface GridApi {
  /** Cell under a pointer position, or null outside every cell. */
  hitTestCell(clientX: number, clientY: number): CellHit | null;
  /**
   * Top-left cell of the current cell selection, or null when no cell is
   * highlighted. Null is the signal that a paste of copied items should land
   * next to the originals instead of in a chosen cell (F-34).
   */
  selectedCell(): SelectedCell | null;
  /** Clear the grid's own selection (used when a floating element is selected). */
  clearSelection(): void;
  /**
   * Select ONE cell by its stable ids — a share link's `?cell=` (F-123,
   * features/board/useShareDeepLink). False when the ids are not in the
   * current layout (deleted, or not synced yet), so the caller can retry.
   */
  selectCell(rowId: string, colId: string): boolean;
  /** Return keyboard focus to the grid wrapper (F-27 popover close). */
  focus(): void;
}

/**
 * One published grid layout pass (F-52 comment anchoring, docs/04 §3): stable
 * row/column ids in visual order plus the board-unit track edges they render
 * at (wrapper-relative; `originX`/`originY` lift them to board space). The
 * comment pins layer resolves cell anchors against this — the same reactive
 * layout state the table renders from, so pins follow sorts, inserts and
 * resizes with no event wiring. Edges already reflect F-26 auto-grown sizes.
 *
 * F-10: `rowIds`/`rowEdges` cover EVERY row, not just the mounted window, and
 * the edges are DERIVED (predicted + measured-on-mount cache) rather than read
 * back from the DOM — an unmounted row has no node to measure, and docs/07 C4
 * reads "id missing from a PRESENT layout" as "that row was deleted".
 */
export interface GridLayoutSnapshot {
  sheetId: string;
  rowIds: readonly string[];
  colIds: readonly string[];
  /**
   * `rowIds[i] → i`. Published because EVERY anchor resolution needs it and a
   * scan does not scale to the F-10 row target: `indexOf`/`includes` over
   * `rowIds` is O(rows), and it runs per pin, per element, per render — on the
   * peers-awareness path (a peer's cursor ticks ~30×/s) and on every zoom
   * frame. At 10k rows that quietly eats part of what windowing bought. The
   * grid builds this map for its own use anyway (`rowIndexById`), so publishing
   * it costs nothing and makes every consumer O(1).
   *
   * It is also the membership test: docs/07 C4's "an id missing from a PRESENT
   * layout means the row was deleted" is `!rowIndex.has(id)`.
   */
  rowIndex: ReadonlyMap<string, number>;
  /** `colIds[i] → i` — same contract as `rowIndex`. */
  colIndex: ReadonlyMap<string, number>;
  /** colEdges[i] = left edge of column i; colEdges[n] = right content edge. */
  colEdges: readonly number[];
  /** rowEdges[i] = top edge of row i; rowEdges[n] = bottom content edge. */
  rowEdges: readonly number[];
  /** GRID_ORIGIN — the wrapper's position in board units. */
  originX: number;
  originY: number;
}

interface CellIds {
  rowId: string;
  colId: string;
}

interface SelectionIds {
  anchor: CellIds;
  head: CellIds;
}

interface EditingState {
  /** Stable ids captured at editor OPEN (docs/07 C4): a remote structural
   * change mid-edit must never retarget the commit. */
  rowId: string;
  colId: string;
  /** 'caret' = Enter/double-click (arrows move the caret);
   *  'replace' = typing started it (arrows commit & navigate — Sheets). */
  mode: "caret" | "replace";
  /** Cell text at editor OPEN — an unchanged commit writes NOTHING (an
   * identical-value write would still clear `st` (F-70.5) and steal
   * last-writer over a concurrent remote edit of the same key). */
  initial: string;
  value: string;
}

/** F-13 add-affordance hover state. `beforeId` = STABLE id of the row/column
 * just after the hovered boundary (undefined = append-at-end); captured at
 * hover time and re-resolved at click time so a remote structural change
 * between hover and click can't shift the insertion point (C4-style guard). */
interface AddHint {
  kind: "col" | "row";
  beforeId: string | undefined;
  x: number;
  y: number;
}

interface HoverHandle {
  kind: "col" | "row";
  index: number;
}

/**
 * One-finger touch gesture on the table (mobile controls, deliberately NOT
 * desktop's): the press starts "pending" (a possible tap) and becomes a PAN of
 * the board camera once it travels past the slop — Sheets-style, chosen over
 * desktop's drag-marquee because on a phone the table fills the screen and
 * swiping it is how you move around. Tap-up selects the cell, double-tap
 * edits, long-press opens the context menu, and range selection lives on the
 * selection's corner handles instead of the marquee.
 */
interface TouchNav {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  lastClientX: number;
  lastClientY: number;
  cell: CellIds;
  mode: "pending" | "pan";
}

/** A drag of one of the touch selection-extend corner handles. */
interface SelHandleDrag {
  pointerId: number;
  /** The FIXED opposite corner the selection extends from. */
  anchor: CellIds;
}

interface PillDrag {
  kind: "col" | "row";
  id: string;
  /** Index at pointerdown — display only; the drop commit re-resolves the
   * dragged id's CURRENT index (a remote move mid-drag must not shift the
   * drop by one — docs/07 C4 discipline). */
  from: number;
  /** F-28: false for the locked source column — the press can select and open
   * the menu, but never becomes a reorder drag. */
  reorderable: boolean;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  moved: boolean;
  boundary: number;
}

interface ResizeDrag {
  kind: "col" | "row";
  /** All tracks this drag resizes: the dragged one, or every column/row of a
   * full-track multi-selection containing it (F-14 "resize all together"). */
  ids: readonly string[];
  pointerId: number;
  startClient: number;
  /** Size at drag start (board px) — EFFECTIVE, i.e. what the user sees,
   * including F-26 auto-widen/auto-grow. Derived state: committing it on a
   * zero-movement click would write the auto-fit size into the CRDT as a
   * manual floor (docs/04 §5 forbids that), so the commit is skipped unless
   * the drag actually changed the size. */
  base: number;
  last: number;
  /** Manual floor from contained elements (F-26/F-36 "cell contains its
   * elements"): the max right/bottom edge over every element anchored to any
   * track in `ids`. The drag can never shrink below it — to make a track
   * smaller than this, the user has to move/resize the elements inside it
   * first. 0 when no track in the group has cell-anchored elements. */
  elementFloor: number;
}

/**
 * Drive a pill/resize drag from WINDOW listeners instead of the affordance
 * element: if the element unmounts mid-drag (remote structural change removes
 * the hovered column, F-45 freeze unmounts the strips), its pointerup/cancel
 * would never fire and the drag ref would stay set forever — killing every
 * hover affordance and leaving the drop indicator on screen. The refs always
 * hold the latest render's handlers. Returns the detach function.
 */
function attachWindowPointerListeners(
  moveRef: MutableRefObject<(event: PointerEvent) => void>,
  upRef: MutableRefObject<(event: PointerEvent) => void>,
): () => void {
  const onMove = (event: PointerEvent) => moveRef.current(event);
  const onUp = (event: PointerEvent) => upRef.current(event);
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onUp);
  return () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
  };
}

/**
 * F-10 windowing: the mounted row range for the CURRENT camera, recomputed
 * from the camera store OUTSIDE React's render (a `useStore` selector cannot
 * return a fresh object without looping) and published as state only when the
 * range actually CHANGES.
 *
 * That last part is the whole trick. The camera store ticks on every pan
 * frame, but the window only moves when a row edge crosses the buffer
 * boundary — so a pan re-renders the table roughly once per row of travel
 * instead of once per frame, and small pans/zooms cost nothing at all. Pan was
 * already at the compositor baseline (docs/10 §1) and must stay there: this
 * hook is what keeps windowing from making pan WORSE than it was.
 */
function useRowWindow(canvas: CanvasStore, rowEdges: readonly number[]): RowWindow {
  const edgesRef = useRef(rowEdges);
  edgesRef.current = rowEdges;
  const [win, setWin] = useState<RowWindow>(() =>
    bufferedRowWindow(rowEdges, canvas.getState().camera, canvas.getState().viewport, GRID_ORIGIN.y),
  );
  useEffect(() => {
    const recompute = () => {
      const { camera, viewport } = canvas.getState();
      const next = bufferedRowWindow(edgesRef.current, camera, viewport, GRID_ORIGIN.y);
      setWin((prev) => (rowWindowsEqual(prev, next) ? prev : next));
    };
    // `rowEdges` is a dep, so this also re-runs whenever the geometry itself
    // changes (a row grew, a column resized, rows were inserted) — the window
    // is a function of the edges as much as of the camera.
    recompute();
    return canvas.subscribe(recompute);
  }, [canvas, rowEdges]);
  return win;
}

export interface GridTableProps {
  session: BoardSession;
  sheetId: string;
  /** The camera store — F-10 windowing reads the visible board rect from it
   * (subscribed outside render; see `useRowWindow`). */
  canvas: CanvasStore;
  /** Camera zoom — used ONLY for pointer-delta math and counter-scaling the
   * hover affordances; rendering itself is in board units under the CSS
   * transform. */
  zoom: number;
  /**
   * The shared bulk-write UI store (F-90 import + F-16 large paste). A paste
   * big enough to be chunked publishes its progress here so it renders the same
   * modal bar as an import, and reads it to refuse starting while another bulk
   * write is mid-flight — see `applyPastedText`.
   */
  bulkUi: ImportUiStore;
  /** Receives the imperative grid API (F-30 drop targeting). */
  gridApi?: MutableRefObject<GridApi | null>;
  /** Any pointer interaction with the grid (deselects floating elements). */
  onGridPointerDown?: () => void;
  /**
   * F-27: publishes the toolbar anchor for the current cell selection —
   * board-space bounding rect + stable range ids — and null whenever the
   * toolbar must hide (no selection, editing, drag-selecting, resizing,
   * reordering, frozen). Called from an effect on the grid's own layout
   * state, so it already reflects F-26 auto-grown sizes.
   */
  onToolbarAnchor?: (anchor: CellsToolbarSelection | null) => void;
  /**
   * F-52: publishes every layout pass (ids + edges) for comment-pin anchor
   * resolution; null on unmount so a stale layout is never mistaken for
   * evidence that rows were deleted (docs/07 C4 degradation is gated on a
   * PRESENT layout that lacks the id).
   */
  onLayout?: (layout: GridLayoutSnapshot | null) => void;
  /**
   * F-34 in-cell item box, live (null = the gesture ended, or turned into a
   * cell range). The grid decides the GESTURE because only it has the cell
   * geometry; the canvas shell owns the selection and paints the box through
   * the same overlay the background marquee uses.
   */
  onCellMarquee?: (marquee: CellMarquee | null) => void;
  /** The box the drag finished with, having never left its start cell. */
  onCellMarqueeCommit?: (marquee: CellMarquee) => void;
}

/**
 * F-34 in-cell item box (decided 2026-08-30): a left-drag that never leaves the
 * cell it started in selects the ITEMS anchored to that cell instead of
 * dragging a cell range — the one gesture the grid had spare, since a drag
 * confined to one cell selects exactly the 1×1 range a plain click already
 * gives. Leaving the cell (at any point, even after the box appeared) makes it
 * the F-15 cell-range drag it has always been, and the decision is final: the
 * gesture must not flip back and forth as the pointer wanders.
 *
 * Points are ABSOLUTE board units (GRID_ORIGIN already added), so they drop
 * straight into `elementsUi.setMarquee` alongside the background marquee's.
 */
export interface CellMarquee {
  /** The cell the drag started in — the ONLY cell whose items it may select. */
  rowId: string;
  colId: string;
  from: Point;
  to: Point;
}

/** Board-unit point (local to this module's public surface). */
interface Point {
  x: number;
  y: number;
}

/** Wrapper-relative board-unit rect of one cell. */
interface CellBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The in-flight table drag-select. `mode` starts "pending" — the gesture is
 * not yet a cell range NOR an item box — and is decided by the first move that
 * either leaves the start cell ("cells") or travels far enough inside it
 * ("items"); see `CellMarquee`.
 */
interface SelectDrag {
  pointerId: number;
  cell: CellIds;
  /** Press point and the start cell's box, wrapper-relative board units.
   * Null when the press landed outside the measured extent — the drag then
   * behaves exactly as it did before item boxes existed (cell range only). */
  start: Point | null;
  box: CellBox | null;
  /** Last point still INSIDE the start cell — what an item box commits with,
   * so a pointerup that lands a hair outside cannot lose the box. */
  last: Point | null;
  mode: "pending" | "cells" | "items";
}

/** Is a wrapper-relative board point inside this cell? Edges count as in. */
function withinCell(box: CellBox, pt: Point): boolean {
  return pt.x >= box.left && pt.x <= box.right && pt.y >= box.top && pt.y <= box.bottom;
}

/** Wrapper-relative points → the ABSOLUTE board box the canvas overlay wants. */
function cellMarqueeOf(cell: CellIds, from: Point, to: Point): CellMarquee {
  return {
    rowId: cell.rowId,
    colId: cell.colId,
    from: { x: GRID_ORIGIN.x + from.x, y: GRID_ORIGIN.y + from.y },
    to: { x: GRID_ORIGIN.x + to.x, y: GRID_ORIGIN.y + to.y },
  };
}

export function GridTable(props: GridTableProps) {
  return <SheetTable key={props.sheetId} {...props} />;
}

function SheetTable({
  session,
  sheetId,
  canvas,
  zoom,
  bulkUi,
  gridApi,
  onGridPointerDown,
  onToolbarAnchor,
  onLayout,
  onCellMarquee,
  onCellMarqueeCommit,
}: GridTableProps) {
  const structureVersion = useStore(session.store, (s) => s.structureVersion);
  const frozen = useStore(session.store, (s) => s.frozen);
  const peers = useStore(session.store, (s) => s.peers);
  const board = session.board;
  const inv = 1 / zoom;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  /** When the editor's last IME composition ended — see onEditorKeyDown. */
  const compositionEndedAt = useRef(Number.NEGATIVE_INFINITY);

  const [cellsTick, setCellsTick] = useState(0);
  const [selection, setSelectionState] = useState<SelectionIds | null>(null);
  const selectionRef = useRef<SelectionIds | null>(null);
  selectionRef.current = selection;
  const [editing, setEditing] = useState<EditingState | null>(null);
  const editingRef = useRef<EditingState | null>(null);
  editingRef.current = editing;
  /**
   * F-17-marks: the character range highlighted INSIDE the open editor — what
   * its format bar acts on, and whether there is one at all. Null for a bare
   * caret: formatting acts on a range, and a bar over a caret would float
   * over the text for no reason.
   */
  const [editorSel, setEditorSel] = useState<{ start: number; end: number } | null>(null);
  /**
   * F-17-type: the caret offset in the open editor, or null for "not in the
   * text" (blurred). Its own state rather than `editorSel`, which is null for
   * a BARE caret — and a bare caret is exactly the situation the `/` menu
   * exists for. Null must not be a sentinel index: a negative offset would
   * still slice a prefix and could re-open the menu on an earlier `/`.
   */
  const [editorCaret, setEditorCaret] = useState<number | null>(null);
  /** Which row of the `/` menu is armed. */
  const [typeMenuActive, setTypeMenuActive] = useState(0);
  /** True between compositionstart and compositionend (an IME is mid-word). */
  const editorComposing = useRef(false);
  /** The open editor's rendered height in board units (it auto-grows, F-26) —
   *  the mirror layer's height, and where the format bar goes when it flips
   *  below. */
  const [editorHeight, setEditorHeight] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const selectDragRef = useRef<SelectDrag | null>(null);
  /** Touch: one-finger tap-or-pan gesture on the table (mobile controls). */
  const touchNavRef = useRef<TouchNav | null>(null);
  /** Touch: pending long-press timer (context menu), if armed. */
  const longPressRef = useRef<number | null>(null);
  /** Touch: previous tap, for double-tap-to-edit detection. */
  const lastTapRef = useRef<{ time: number; rowId: string; colId: string } | null>(null);
  /** Touch: drag of a selection-extend corner handle. */
  const selHandleRef = useRef<SelHandleDrag | null>(null);
  /** Latest openCellMenuAt — the long-press timer fires through this ref
   * (the opener is declared later in the file, next to the menu builders). */
  const openCellMenuAtRef = useRef<(cell: CellIds, x: number, y: number) => void>(() => {});
  /** Live-local sizes during an F-14 drag — board px, cleared on commit. */
  const [widthOverrides, setWidthOverrides] = useState<ReadonlyMap<string, number>>(new Map());
  const [heightOverrides, setHeightOverrides] = useState<ReadonlyMap<string, number>>(new Map());
  /** F-42: cells where the user chose "Edit anyway" despite a remote claim. */
  const [lockOverrides, setLockOverrides] = useState<ReadonlySet<string>>(new Set());
  const [hover, setHover] = useState<HoverHandle | null>(null);
  const [addHint, setAddHint] = useState<AddHint | null>(null);
  const [dragIndicator, setDragIndicator] = useState<{ kind: "col" | "row"; boundary: number } | null>(null);
  const pillDragRef = useRef<PillDrag | null>(null);
  const resizeRef = useRef<ResizeDrag | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  /** F-23/F-12b: id of the column whose rename dialog is open, or null. */
  const [renaming, setRenaming] = useState<string | null>(null);
  /**
   * F-10: bumped when a MOUNTED row turns out not to render at the height we
   * predicted for it, so the edges re-derive from the corrected cache. Cannot
   * loop: `predictRowHeight` ceils, so a measurement can only ever be TALLER
   * than the prediction it replaces; feeding it back raises the `min-height`,
   * the next layout reports the same number, and `setMeasured` returns false.
   * On the measured realistic corpus this never fires at all (docs/10 §2c).
   */
  const [heightsVersion, setHeightsVersion] = useState(0);

  /* ---------------- layout derived from the doc (C1 order) ---------- */

  const { rowIds, colInfos } = useMemo(() => {
    void structureVersion; // re-derive on any structural Y change
    const infos: ColumnInfo[] = [];
    for (const colId of board.sortedColIds(sheetId)) {
      const info = board.getColumn(sheetId, colId);
      if (info) infos.push(info);
    }
    return { rowIds: board.sortedRowIds(sheetId), colInfos: infos };
  }, [board, sheetId, structureVersion]);

  const colIds = useMemo(() => colInfos.map((c) => c.id), [colInfos]);

  /**
   * F-28: index of the locked "Original Script" source column, or -1 on a
   * board seeded before F-28 (nothing is locked ⇒ no constraint anywhere).
   * Derived from the same `colInfos` pass the table renders from, so the UI's
   * idea of the lock can never lag the doc's.
   */
  const sourceColIndex = useMemo(() => colInfos.findIndex((c) => c.locked), [colInfos]);

  // Also published on the layout snapshot (GridLayoutSnapshot.rowIndex), which
  // is why they are built with the shared helper rather than inline.
  const rowIndexById = useMemo(() => indexById(rowIds), [rowIds]);
  const colIndexById = useMemo(() => indexById(colIds), [colIds]);

  /* ---------------- F-26 run-width pass (derived, board units) ------- */

  /** Raw text width in the table font, memoized (core/measure.ts) — shared by
   * the F-26 run pass, the F-14 autofit, and the row-height predictor, so one
   * warm cache serves all three. */
  const measureTextWidth = boardTextWidth;
  const runWidthOf = useMemo(() => createRunWidthCache(boardTextWidth), []);

  /** Text of a cell as the table renders it ("" for empty/legacy image). */
  const textOfCell = useCallback(
    (rowId: string, colId: string): string => {
      const snapshot = board.getCell(sheetId, rowId, colId);
      if (snapshot === undefined || snapshot.v === null) return "";
      // Images are ALWAYS elements now (F-30, inline image cell value retired):
      // a cell never widens or grows for an inline image. A stray legacy image
      // value reads as empty, and an F-126 BGM stem as its title — the shared
      // `cellPlainText` is the one place that mapping lives.
      return cellPlainText(snapshot.v);
    },
    [board, sheetId],
  );

  /**
   * F-26 widths and row heights are DERIVED state, recomputed identically by
   * every client — but they used to be recomputed by walking EVERY cell of
   * every column on EVERY cell change. At 10k rows that is 20 000 cells per
   * keystroke and per arriving F-70 translation batch. Both passes are now
   * incremental (docs/10 §3): O(rows) once per column/row, then O(damaged
   * cells) per edit. See core/grid-sizing.ts.
   */
  const runTrackerRef = useRef(new ColumnRunTracker());
  const heightCacheRef = useRef(new RowHeightCache(boardTextWidth));
  /** The structure version the trackers were last pruned at (row deletes). */
  const prunedAtRef = useRef(-1);

  /* ---------------- F-26/F-36 contained-element cell growth ---------- */

  // Contained-element cell sizing is PERSISTED (docs/04 §5): when an element
  // is created in a cell the acting client ratchets `rowMeta.height` /
  // `colMeta.width` UP, and a drag-DROP readjusts both touched cells in BOTH
  // directions (session.fitCell, 2026-07-17) — each in the same transaction as
  // the element mutation. The grid therefore reads those STORED sizes as the
  // element floor here (via `manual`/`rowMinHeight`, exactly like a manual
  // F-14 resize) — there is deliberately NO live derived shrink pass: cell
  // sizes only change when a mutation commits them.

  /**
   * Reconcile both trackers with the sheet's live rows/columns. Required for
   * correctness, not just memory: docs/07 C4 keeps a deleted row's CELLS as
   * orphaned entries (swept later by snapshot compaction), and neither a
   * deleted nor a RESURRECTED row writes a cell — so nothing on the damage path
   * can tell either tracker that the row set moved. Runs once per structural
   * change, never per edit.
   *
   * The row-height cache only needs the DROP half: a row it has forgotten is
   * simply re-predicted on the next `heightOf`. The run tracker needs both (see
   * `ColumnRunTracker.syncRows`), which is why it gets the reader.
   */
  const syncTrackedRows = useCallback(() => {
    if (prunedAtRef.current === structureVersion) return;
    prunedAtRef.current = structureVersion;
    const live = new Set(rowIds);
    // Columns first: a column deleted in this same change must not be re-read
    // for the rows syncRows sees as new.
    const liveCols = new Set(colIds);
    for (const colId of [...runTrackerRef.current.trackedColumns()]) {
      if (!liveCols.has(colId)) runTrackerRef.current.deleteColumn(colId);
    }
    runTrackerRef.current.syncRows(live, (colId, rowId) => runWidthOf(textOfCell(rowId, colId)));
    heightCacheRef.current.retainRows(live);
  }, [structureVersion, rowIds, colIds, runWidthOf, textOfCell]);

  /** Effective column widths (board px): manual/element floor + run widening. */
  const colWidths = useMemo(() => {
    void cellsTick; // content edits can move run widths (via the damage path)
    syncTrackedRows();
    const tracker = runTrackerRef.current;
    return colInfos.map((info) => {
      // O(rows) the first time a column is seen; O(1) afterwards — the damage
      // listener keeps the running max up to date.
      if (!tracker.has(info.id)) {
        tracker.build(info.id, rowIds, (rowId) => runWidthOf(textOfCell(rowId, info.id)));
      }
      // The stored width (manual F-14 min AND the F-36 element sizing —
      // create-time ratchet / drop-time fit) is the floor; text auto-fit
      // widens past it but never shrinks it.
      const manual = widthOverrides.get(info.id) ?? info.width;
      return autoColumnWidth(tracker.maxOf(info.id), manual);
    });
  }, [rowIds, colInfos, widthOverrides, cellsTick, runWidthOf, textOfCell, syncTrackedRows]);

  const colEdges = useMemo(() => edgesFromSizes(colWidths), [colWidths]);

  /* ---------------- row heights for EVERY row (F-10 windowing) ------- */

  /**
   * The stored height FLOOR of every row (`rowMeta.height`: the F-14 manual
   * resize and the F-36 element ratchet), or the F-14 drag's live override.
   *
   * Split out of `rowHeights` because it is the only part of it that reads the
   * DOC, and it must not be on the per-edit path: `board.getRow` is a
   * `getRowMeta` + `Y.Map.get` + a fresh `RowInfo` allocation per row, so
   * re-deriving it inside `rowHeights` cost ~20k Yjs reads and 10k allocations
   * on every keystroke and every arriving F-70 batch at 10k rows — a cost the
   * docs/10 §1 "73.1 ms → 0.6 ms" figure does not include (that spike is cut
   * free of Yjs). A floor only ever changes with `rowMeta`, and the session's
   * `rowMetaObserver` already bumps `structureVersion` for exactly that — so
   * this recomputes once per structural change, and a cell edit reuses it.
   */
  const rowFloors = useMemo(() => {
    void structureVersion;
    return rowIds.map(
      (rowId) =>
        heightOverrides.get(rowId) ?? board.getRow(sheetId, rowId)?.height ?? DEFAULT_ROW_HEIGHT,
    );
  }, [board, sheetId, rowIds, heightOverrides, structureVersion]);

  /**
   * The height of every row, mounted or not (F-10 constraint (a)). Rows that
   * have mounted report their real height; the rest are predicted from the
   * text (core/measure.ts). Auto-fit sizes are NEVER written to the CRDT
   * (docs/04 §5) — only the stored floor (`rowFloors`) comes from the doc,
   * exactly as before.
   */
  const rowHeights = useMemo(() => {
    void cellsTick;
    void heightsVersion;
    syncTrackedRows();
    const cache = heightCacheRef.current;
    // A width change re-wraps every row, so this drops the whole cache and
    // re-predicts all of them. MEASURED at 10k rows × 2 cols of wrapped script:
    // ~100 ms. It is the one remaining O(rows × cols) hot path, and it fires
    // per frame of an F-14 column-resize DRAG (~10 fps while dragging at 10k).
    // Accepted, not hidden: every row's height genuinely changes, and the block
    // cannot be positioned without the heights ABOVE it, so any correct live
    // preview pays this. The old full-DOM path paid more (it re-laid out all
    // 10 000 rows and then read every offsetTop back). Everything windowing was
    // for — edits, zoom, mount, pan — is off this path.
    cache.setSignature(heightSignature(colWidths, FONT_VERSION));
    return rowIds.map((rowId, i) =>
      // The stored floor (manual F-14 min AND the F-36 element sizing —
      // create-time ratchet / drop-time fit); text auto-grow grows past it.
      cache.heightOf(
        rowId,
        rowFloors[i] ?? DEFAULT_ROW_HEIGHT,
        () => colIds.map((colId) => textOfCell(rowId, colId)),
        colWidths,
      ),
    );
  }, [rowIds, colIds, colWidths, rowFloors, cellsTick, heightsVersion, textOfCell, syncTrackedRows]);

  const rowEdges = useMemo(() => edgesFromSizes(rowHeights), [rowHeights]);
  const tableW = (colEdges[colEdges.length - 1] ?? TABLE_BORDER) + TABLE_BORDER;
  const tableH = (rowEdges[rowEdges.length - 1] ?? TABLE_BORDER) + TABLE_BORDER;

  /* ---------------- the mounted row range (F-10) --------------------- */

  const rowWin = useRowWindow(canvas, rowEdges);
  const windowEmpty = isEmptyRowWindow(rowWin);
  const firstRow = rowWin.first;
  const lastRow = rowWin.last;

  /* ---------------- measured-height correction (hybrid truth) -------- */

  /**
   * Read back what the MOUNTED rows really rendered at (docs/10 §2c: "the
   * measured cache is truth"). Only the mounted block is touched — ~50 rows,
   * not 10 000 — which is what turns the old 20.4 ms full measure pass into
   * noise. A correction is a no-op for 100 % of the measured realistic corpus;
   * it exists for the ±1-line cases the emulator cannot get right without ICU
   * (Thai/Khmer), which then self-heal.
   */
  /**
   * What the MOUNTED BLOCK currently holds, mirrored from the last commit.
   *
   * This must never be read from a render closure. `correctMeasuredHeights`
   * maps a DOM child index back to a row id (`children[(r - first) * nCols]`),
   * and the ResizeObserver below fires on the block's own size changes — which
   * is to say, on every window change. A closure captured before that change
   * would map the NEW children against the OLD `first` and write each row's
   * measured height onto a DIFFERENT row.
   *
   * MEASURED, not theorised: with the window in a render closure, panning across
   * a 10 000-row board corrupted ~40 rows per traverse (a row's height jumping
   * to its neighbour's — e.g. an empty-target row taking a wrapped row's 98 px),
   * and the corruption survived, because a wrong "measured" height is treated as
   * truth. Writing the mirror in the LAYOUT effect (after the DOM is committed,
   * before paint) is what keeps ref and DOM in lockstep.
   */
  const domBlockRef = useRef<{
    first: number;
    last: number;
    rowIds: readonly string[];
    nCols: number;
  } | null>(null);

  const correctMeasuredHeights = useCallback(() => {
    const table = tableRef.current;
    const block = domBlockRef.current;
    if (!table || block === null || block.nCols === 0) return;
    const cache = heightCacheRef.current;
    let changed = false;
    for (let r = block.first; r <= block.last; r++) {
      const rowId = block.rowIds[r];
      if (rowId === undefined) continue;
      // Cells are laid out row-major within the mounted block.
      const cell = table.children[(r - block.first) * block.nCols] as HTMLElement | undefined;
      if (!cell) break;
      // offsetHeight, not getBoundingClientRect: the latter is in SCREEN px
      // (the camera's scale transform applies to it), so under zoom it would
      // feed zoom-rounded noise back into board-unit geometry.
      if (cache.setMeasured(rowId, cell.offsetHeight)) changed = true;
    }
    // The ref-mirrored bail-out of the old measure pass, kept for the same
    // reason: this runs in a no-deps layout effect, and an unconditional
    // setState here schedules a nested update on every commit — React's
    // update-depth limit trips once sync/store updates land in the same window.
    if (changed) setHeightsVersion((v) => v + 1);
  }, []);

  // After every commit (cell edits re-wrap rows synchronously) …
  useLayoutEffect(() => {
    domBlockRef.current = windowEmpty
      ? null
      : { first: firstRow, last: lastRow, rowIds, nCols: colIds.length };
    correctMeasuredHeights();
  });
  // … and when anything moves a mounted row's height without a re-render.
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const observer = new ResizeObserver(() => correctMeasuredHeights());
    observer.observe(table);
    return () => observer.disconnect();
  }, [correctMeasuredHeights]);

  /* ---------------- fine-grained cell change subscription ------------ */

  useEffect(() => {
    // The damage path is what keeps the two derived passes incremental: a cell
    // edit invalidates exactly its row's height and updates exactly its
    // column's running max, instead of re-deriving the whole sheet.
    return session.onCellsChanged((keys) => {
      const tracker = runTrackerRef.current;
      const cache = heightCacheRef.current;
      for (const key of keys) {
        const parsed = parseCellKey(key);
        if (parsed === null) continue;
        cache.invalidateRow(parsed.rowId);
        tracker.update(parsed.colId, parsed.rowId, runWidthOf(textOfCell(parsed.rowId, parsed.colId)));
      }
      setCellsTick((t) => t + 1);
    });
  }, [session, runWidthOf, textOfCell]);

  /* ---------------- presence: soft-lock claims + highlights ---------- */

  const remoteClaims = useMemo(() => {
    const map = new Map<string, RemotePeer>();
    for (const peer of peers) {
      const claim = peer.editingCell;
      if (claim && claim.sheetId === sheetId) {
        map.set(cellKey(claim.rowId, claim.colId), peer);
      }
    }
    return map;
  }, [peers, sheetId]);

  // Drop stale "Edit anyway" overrides once the remote claim is gone.
  useEffect(() => {
    setLockOverrides((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      for (const key of prev) if (remoteClaims.has(key)) next.add(key);
      return next.size === prev.size ? prev : next;
    });
  }, [remoteClaims]);

  /* ---------------- selection (F-15, published for F-41) ------------- */

  /**
   * F-27: the last pointer position over the grid, in wrapper-relative board
   * units — recorded on every pointer down and up (capture phase, so a click
   * on a row handle, a cell, or a selection handle all count) so the toolbar
   * anchor can say where the HAND was when the selection was made. A whole-row
   * selection is as wide as the table; the pill belongs over the row handle
   * that was clicked, not at the table's horizontal centre (2026-09-03).
   * Cleared by any key press: a keyboard-extended selection has no pointer to
   * speak of, and a stale one would pin the pill to wherever the mouse last
   * rested.
   */
  const selectPointerRef = useRef<{ x: number; y: number } | null>(null);

  const setSelection = useCallback(
    (next: SelectionIds | null) => {
      setSelectionState(next);
      session.setSelection(
        next === null
          ? undefined
          : {
              sheetId,
              anchor: { rowId: next.anchor.rowId, colId: next.anchor.colId },
              head: { rowId: next.head.rowId, colId: next.head.colId },
            },
      );
    },
    [session, sheetId],
  );

  /** Current selection as an index range, or null if any id vanished (C4). */
  const selRange = useMemo(() => {
    if (selection === null) return null;
    const aRow = rowIndexById.get(selection.anchor.rowId);
    const aCol = colIndexById.get(selection.anchor.colId);
    const hRow = rowIndexById.get(selection.head.rowId);
    const hCol = colIndexById.get(selection.head.colId);
    if (aRow === undefined || aCol === undefined || hRow === undefined || hCol === undefined) {
      return null;
    }
    return { ...normalizeRange(aRow, aCol, hRow, hCol), aRow, aCol };
  }, [selection, rowIndexById, colIndexById]);

  const selectCellAt = useCallback(
    (row: number, col: number) => {
      const rowId = rowIds[row];
      const colId = colIds[col];
      if (rowId === undefined || colId === undefined) return;
      const cell = { rowId, colId };
      setSelection({ anchor: cell, head: cell });
    },
    [rowIds, colIds, setSelection],
  );

  /* ---------------- editing (F-12, F-42 soft lock, C4 guards) -------- */

  const closeEditor = useCallback(() => {
    setEditing(null);
    editingRef.current = null;
    setEditorSel(null);
    setEditorCaret(null);
    session.setEditingCell(undefined);
    wrapRef.current?.focus({ preventScroll: true });
  }, [session]);

  const commitEditor = useCallback(() => {
    const edit = editingRef.current;
    if (edit === null) return;
    const { rowId, colId, value, initial } = edit;
    if (value === initial) {
      // Nothing changed (opened to read, or "Edit anyway" then no typing):
      // write NOTHING. An identical-value write would still clear `st`
      // (F-70.5 — the cell would silently stop counting as machine-translated)
      // and steal last-writer over a concurrent remote edit of the key (LWW).
      closeEditor();
      return;
    }
    if (session.store.getState().frozen) {
      // F-45: the offline freeze engaged while the editor was open (the
      // textarea stays interactive). setCellValue would silently no-op via
      // canEdit() — surface the loss like the C4 path below instead.
      showToast(
        `You went offline — your edit was not saved: "${truncate(value, 80)}"`,
      );
    } else if (!board.hasRow(sheetId, rowId) || !board.hasColumn(sheetId, colId)) {
      // docs/07 C4.1: the container of the active edit vanished mid-edit.
      const what = board.hasRow(sheetId, rowId) ? "column" : "row";
      showToast(
        `This ${what} was deleted by another user — your edit was not saved: ` +
          `"${truncate(value, 80)}"`,
      );
    } else {
      session.setCellValue(sheetId, rowId, colId, value === "" ? null : value);
    }
    closeEditor();
  }, [board, session, sheetId, closeEditor]);

  const openEditor = useCallback(
    (rowId: string, colId: string, mode: "caret" | "replace", seed?: string) => {
      if (frozen) return;
      const snapshot = board.getCell(sheetId, rowId, colId);
      if (snapshot !== undefined && isImageCellValue(snapshot.v)) {
        return; // image cells have no text editor; Del/Backspace clears them
      }
      // F-126: a BGM stem is edited through its chip (source + title together),
      // never as free text — typing over the title would leave a stem pointing
      // at music it is no longer named after.
      if (snapshot !== undefined && isBgmCellValue(snapshot.v)) return;
      const key = cellKey(rowId, colId);
      const claim = remoteClaims.get(key);
      if (claim !== undefined && !lockOverrides.has(key)) {
        // F-42 soft lock: block edit mode, offer the explicit override.
        showToast(`${claim.user.name} is editing this cell`, {
          actionLabel: "Edit anyway",
          onAction: () =>
            setLockOverrides((prev) => {
              const next = new Set(prev);
              next.add(key);
              return next;
            }),
        });
        return;
      }
      const current =
        snapshot === undefined || snapshot.v === null ? "" : String(snapshot.v);
      setEditing({
        rowId,
        colId,
        mode,
        initial: current,
        value: mode === "replace" ? (seed ?? "") : current,
      });
      // A range highlighted in the cell we just left is not a range in this one.
      setEditorSel(null);
      // Nor is a half-typed `/` command — the focus effect below puts the
      // caret at the end of this cell's own text.
      setEditorCaret(null);
      // Claim the cell for ourselves while the editor is open (F-42).
      session.setEditingCell({ sheetId, rowId, colId });
    },
    [board, session, sheetId, frozen, remoteClaims, lockOverrides],
  );

  // Focus the textarea when the editor opens; caret at the end.
  useLayoutEffect(() => {
    if (editing === null) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
    setEditorCaret(el.value.length);
    // Intentionally only on open (rowId/colId), not on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.rowId, editing?.colId]);

  // F-26: the overlay grows while typing — never clips.
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.style.height = "auto";
    const height = el.scrollHeight + 2;
    el.style.height = `${height}px`;
    // Published as state because two things OUTSIDE the textarea are sized
    // from it: the F-17-marks mirror behind it (which must cover the same box
    // exactly) and the format bar, when there is no room for it above.
    setEditorHeight(height);
  }, [editing?.value, editing?.rowId, editing?.colId]);

  // Release the F-42 claim if we unmount mid-edit.
  useEffect(() => {
    return () => {
      if (editingRef.current !== null) session.setEditingCell(undefined);
    };
  }, [session]);

  /** Commit the editor and move the selection (Tab/Enter/arrow navigation). */
  const commitAndMove = useCallback(
    (dRow: number, dCol: number) => {
      const edit = editingRef.current;
      if (edit === null) return;
      const row = rowIndexById.get(edit.rowId);
      const col = colIndexById.get(edit.colId);
      commitEditor();
      if (row !== undefined && col !== undefined) {
        selectCellAt(stepIndex(row, dRow, rowIds.length), stepIndex(col, dCol, colIds.length));
      }
    },
    [commitEditor, selectCellAt, rowIndexById, colIndexById, rowIds.length, colIds.length],
  );

  /* ---------------- F-17-marks in the cell editor -------------------- */

  /**
   * The marks on the cell being edited. Their presence is what switches the
   * editor into its mirrored two-layer form (see `.gt-editor-render`), so it
   * is read live — a mark written from the bar, or by a peer in the script
   * view, engages the mirror on the same tick it lands.
   */
  const editRowId = editing?.rowId;
  const editColId = editing?.colId;
  /**
   * The open cell's marks and F-17-type switches — what its mirror layer
   * paints, and whether it needs one at all (undefined = a plain textarea).
   */
  const editorRanges = useMemo(() => {
    if (editRowId === undefined || editColId === undefined) return undefined;
    const cell = board.getCell(sheetId, editRowId, editColId);
    if (cell === undefined || (cell.marks === undefined && cell.ty === undefined)) return undefined;
    return { marks: cell.marks, types: cell.ty };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mark and kind writes bump cellsTick
  }, [board, sheetId, editRowId, editColId, cellsTick]);

  /**
   * What the editor's format bar shows as ACTIVE. Resolved against the DRAFT —
   * the text the writer is looking at — not the committed cell value: marks
   * re-anchor by quote, so a not-yet-committed keystroke shifts nothing here.
   */
  const editorFormat = useMemo(() => {
    if (editing === null || editorSel === null) return null;
    const range = orderedRange(editorSel.start, editorSel.end);
    if (range === null) return null;
    return readFormatState(
      editing.value,
      (kind) => board.getCellMarks(sheetId, editing.rowId, editing.colId, kind),
      range.start,
      range.end,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mark writes bump cellsTick
  }, [board, sheetId, editing, editorSel, cellsTick]);

  /* ---------------- F-17-type: the `/` menu in the cell editor -------- */

  /**
   * The `/` command the caret sits in, and the kinds matching it. The menu is
   * open only when BOTH exist — a query that names no kind ("1/2 cups")
   * silently closes it rather than showing an empty box that would swallow
   * Enter, exactly as the comment composer's `@` picker does.
   */
  const typeTrigger =
    editing === null || editorCaret === null ? null : slashQueryAt(editing.value, editorCaret);
  // Derived from the admin-defined kinds (features/text-kinds): a kind added
  // on /admin is in the next `/` a writer types.
  const customKinds = useTextKinds();
  const typeMatches =
    typeTrigger === null ? [] : matchTextTypes(typeTrigger.query, textTypeOptions(customKinds));
  const typeMenuOpen = !frozen && typeMatches.length > 0;
  const typeMenuIndex = Math.min(typeMenuActive, typeMatches.length - 1);

  // Re-arm the first row whenever the query changes under it.
  useEffect(() => {
    setTypeMenuActive(0);
  }, [typeTrigger?.start, typeTrigger?.query]);

  /**
   * The menu's live state, for `onEditorKeyDown` to read.
   *
   * Refs rather than callback deps, the same way `editingRef` already works
   * here: `typeMatches` is a fresh array every render, so depending on it
   * would rebuild the editor's key handler on every keystroke.
   */
  const typeMenuOpenRef = useRef(false);
  typeMenuOpenRef.current = typeMenuOpen;
  const typeMenuRef = useRef<{ options: TextTypeOption[]; index: number }>({
    options: [],
    index: 0,
  });
  typeMenuRef.current = { options: typeMatches, index: typeMenuIndex };

  /**
   * Take an option from the `/` menu.
   *
   * A KIND is a switch point in the cell's text (F-17-type): the `/query` is
   * cut out of the draft and the switch lands where the `/` stood. The draft
   * is FLUSHED into the doc in the same transaction as the switch, for the
   * reason `flushEditorDraft` gives below — a switch is an offset into the
   * cell's text, and there is no such thing as a switch into text that does
   * not exist yet. So picking a kind commits what was typed so far, and an
   * Esc afterwards keeps it; `initial` moves up with the flush.
   *
   * A SNIPPET (`/pause` → "[Pause]") is just text: it replaces the `/query`
   * in the draft and rides the ordinary commit, like any other keystroke.
   */
  const pickTextType = useCallback(
    (option: TextTypeOption) => {
      const edit = editingRef.current;
      if (edit === null || typeTrigger === null || editorCaret === null) return;
      const next = applyTextTypePick(edit.value, editorCaret, typeTrigger.start, option);
      let flushed: EditingState = { ...edit, value: next.text };
      if (option.kind === "type") {
        session.setCellTextTypeAt(
          sheetId,
          edit.rowId,
          edit.colId,
          next.text,
          next.caret,
          option.type ?? undefined,
        );
        flushed = { ...flushed, initial: next.text };
      }
      editingRef.current = flushed;
      setEditing(flushed);
      setEditorCaret(next.caret);
      const el = editorRef.current;
      // After React has written the new draft, or the caret lands at the end.
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(next.caret, next.caret);
      });
    },
    [sheetId, session, typeTrigger, editorCaret],
  );
  const pickTextTypeRef = useRef(pickTextType);
  pickTextTypeRef.current = pickTextType;

  /**
   * Push the editor's uncommitted draft into the doc BEFORE a mark is written
   * against it — the grid's half of the script view's `flushBlock`. A mark is
   * character offsets into the CELL text, and the editor holds its value in
   * React state until Enter/Tab/blur, so "type a word, select it, hit ⌘B"
   * would otherwise apply offsets from the draft to the older text in the doc.
   *
   * `initial` moves up with the flush: the flushed value IS the cell's value
   * now, so the eventual commit correctly writes nothing (an identical-value
   * write would still clear `st`, F-70.5, and steal last-writer over a
   * concurrent remote edit of the key).
   *
   * The visible consequence is that formatting text you just typed COMMITS
   * that text, so a later Esc no longer un-types it. That is the only coherent
   * reading: a mark is offsets into the cell's text, and there is no such
   * thing as marking a value that does not exist yet. Undo still takes both
   * back together — the flush and the mark share one transaction.
   */
  const flushEditorDraft = useCallback(() => {
    const edit = editingRef.current;
    if (edit === null || edit.value === edit.initial) return;
    session.setCellValue(sheetId, edit.rowId, edit.colId, edit.value === "" ? null : edit.value);
    const flushed: EditingState = { ...edit, initial: edit.value };
    editingRef.current = flushed;
    setEditing(flushed);
  }, [session, sheetId]);

  /**
   * Run a formatting write over the editor's CHARACTER selection: flush + mark
   * in ONE transaction, so it is one sync unit and one undo step, and the text
   * a mark refers to can never be mid-flight when the mark lands.
   *
   * The range comes off the live textarea rather than `editorSel` state: every
   * control acts on pointerdown with preventDefault precisely so the selection
   * survives the press, and the element is where it survives.
   *
   * Deliberately NOT gated on the F-42 soft lock — the editor is already open,
   * which means the claim is ours (or was explicitly overridden).
   */
  const markEditorSelection = useCallback(
    (apply: (rowId: string, colId: string, start: number, end: number) => void) => {
      const edit = editingRef.current;
      const el = editorRef.current;
      if (edit === null || el === null || frozen) return;
      const range = orderedRange(el.selectionStart, el.selectionEnd);
      if (range === null) return;
      // docs/07 C4.1: the cell's row or column vanished mid-edit. commitEditor
      // reports that on the way out; a mark write simply declines — there is
      // no text left for a range to anchor to.
      if (!board.hasRow(sheetId, edit.rowId) || !board.hasColumn(sheetId, edit.colId)) return;
      session.batch(() => {
        flushEditorDraft();
        apply(edit.rowId, edit.colId, range.start, range.end);
      });
    },
    [board, session, sheetId, frozen, flushEditorDraft],
  );

  const toggleEditorMark = useCallback(
    (kind: "bold" | "strike") => {
      markEditorSelection((rowId, colId, start, end) =>
        session.toggleCellMark(sheetId, rowId, colId, kind, start, end),
      );
    },
    [markEditorSelection, session, sheetId],
  );

  const setEditorMarkColor = useCallback(
    (kind: "color" | "hl", color: string | undefined) => {
      markEditorSelection((rowId, colId, start, end) =>
        session.setCellMarkColor(sheetId, rowId, colId, kind, start, end, color),
      );
    },
    [markEditorSelection, session, sheetId],
  );

  /* ---------------- clipboard (F-16 TSV) ----------------------------- */

  const buildSelectionTsv = useCallback((): string | null => {
    if (selRange === null) return null;
    const matrix: string[][] = [];
    for (let r = selRange.r0; r <= selRange.r1; r++) {
      const row: string[] = [];
      for (let c = selRange.c0; c <= selRange.c1; c++) {
        const rowId = rowIds[r];
        const colId = colIds[c];
        if (rowId === undefined || colId === undefined) {
          row.push("");
          continue;
        }
        const snapshot = board.getCell(sheetId, rowId, colId);
        if (snapshot === undefined || snapshot.v === null) row.push("");
        // An image exports as its placeholder (there is no text to carry); a
        // BGM stem exports as its title, which IS the text a reader wants.
        else if (isImageCellValue(snapshot.v)) row.push(IMAGE_CELL_TEXT_PLACEHOLDER);
        else row.push(cellPlainText(snapshot.v));
      }
      matrix.push(row);
    }
    return matrixToTsv(matrix);
  }, [board, sheetId, selRange, rowIds, colIds]);

  /**
   * Land a block with its top-left on the selection's top-left cell — the one
   * paste engine behind both clipboard flavors (F-16): TSV from Sheets, Excel
   * or any text (`applyPastedText`) and a block copied from a Kola grid
   * (`applyPastedCells`). Only `plan.write` knows what a cell is.
   */
  const applyPaste = useCallback(
    async <T,>(plan: PastePlan<T>) => {
      if (frozen || selection === null) return;
      const matrix = plan.matrix;
      if (matrix.length === 0) return;
      // A paste bigger than one chunk yields between transactions, so a SECOND
      // one (or an import) starting mid-flight would compute its own growth from
      // the row plan this one already invalidated, write into rows this one is
      // also writing, and — because `beginUndoGroup` counts nesting — collapse
      // both into ONE undo item. Refuse instead. Anything at or under a chunk
      // never awaits, so it cannot interleave and needs no bar.
      const chunked = matrix.length > IMPORT_CHUNK_ROWS;
      if (importBusy(bulkUi.getState().phase)) {
        showToast("Another import or paste is already running — wait for it to finish");
        return;
      }
      // Re-resolve the anchor's STABLE ids to their CURRENT indices before
      // writing — a remote insert/delete/move since the selection was made
      // must not shift the paste onto different rows (docs/07 C4).
      const aRow = rowIndexById.get(selection.anchor.rowId);
      const aCol = colIndexById.get(selection.anchor.colId);
      if (aRow === undefined || aCol === undefined) {
        showToast("The paste target was deleted by another user — nothing was pasted");
        return;
      }
      const hRow = rowIndexById.get(selection.head.rowId) ?? aRow;
      const hCol = colIndexById.get(selection.head.colId) ?? aCol;
      const startRow = Math.min(aRow, hRow);
      const startCol = Math.min(aCol, hCol);
      const matrixCols = matrix.reduce((max, r) => Math.max(max, r.length), 0);
      // F-16: a paste NEVER truncates — rows/columns are appended to fit the
      // pasted block (new boards start 2×3, so pasting a Sheets range larger
      // than the table is the common first-use path).
      const allRowIds = [...rowIds];
      const allColIds = [...colIds];
      const claimed: PasteWriteItem<T>[] = [];
      const skipped = new Set<string>();
      let claimantName: string | undefined;

      // F-90: pasting a whole script is a first-class path (F-16 + F-125), so
      // this uses the import's machinery — ONE bulk `insertRows` for the growth
      // (the old `insertRow` loop was O(n²): 93 ms at 2k rows, minutes at 50k)
      // and bounded transactions with a yield between them, so a 10k-line paste
      // fills in live instead of hanging the tab. `beginUndoGroup` is what keeps
      // that one Ctrl+Z, exactly as when it was one transaction.
      // Claimed BEFORE the first write, so the guard above is armed for the
      // whole run — and, for a chunked paste, the modal it renders is what stops
      // a stray edit from merging into the undo group this is about to open.
      if (chunked) bulkUi.getState().setPhase({ kind: "pasting", done: 0, total: matrix.length });
      const endGroup = session.beginUndoGroup();
      try {
        session.batch(() => {
          const needRows = startRow + matrix.length - allRowIds.length;
          if (needRows > 0) allRowIds.push(...session.insertRows(sheetId, needRows));
          // Columns stay a loop: a paste is bounded by the source range's
          // width (Sheets caps at 18k columns, real ones are tens), and there
          // is no measured O(n²) problem at that scale.
          while (allColIds.length < startCol + matrixCols) {
            const id = session.insertColumn(sheetId, allColIds.length);
            if (id === null) return;
            allColIds.push(id);
          }
        });

        for (const chunk of planChunks(matrix.length, IMPORT_CHUNK_ROWS)) {
          const writes: PasteWriteItem<T>[] = [];
          for (let dr = chunk.start; dr < chunk.end; dr++) {
            (matrix[dr] ?? []).forEach((item, dc) => {
              const rowId = allRowIds[startRow + dr];
              const colId = allColIds[startCol + dc];
              if (rowId === undefined || colId === undefined) return;
              const key = cellKey(rowId, colId);
              const claim = remoteClaims.get(key);
              if (claim !== undefined && !lockOverrides.has(key)) {
                // F-42: don't silently overwrite a cell someone is typing in
                // — same block+override treatment as entering edit mode.
                claimed.push({ rowId, colId, item });
                skipped.add(key);
                claimantName = claim.user.name;
                return;
              }
              writes.push({ rowId, colId, item });
            });
          }
          plan.write(writes);
          if (chunked) {
            bulkUi.getState().setPhase({ kind: "pasting", done: chunk.end, total: matrix.length });
          }
          if (chunk.end < matrix.length) await yieldToUi();
        }
        if (plan.afterCells !== undefined) {
          const target: PasteTarget = {
            rowIds: allRowIds.slice(startRow, startRow + matrix.length),
            colIds: allColIds.slice(startCol, startCol + matrixCols),
            startRow,
            startCol,
            skipped,
          };
          session.batch(() => plan.afterCells?.(target));
        }
      } finally {
        endGroup();
        if (chunked) bulkUi.getState().reset();
      }
      if (claimed.length > 0) {
        showToast(skippedClaimMessage(claimed.length, claimantName), {
          actionLabel: "Paste anyway",
          onAction: () => plan.write(claimed),
        });
      }
    },
    [
      session,
      sheetId,
      frozen,
      selection,
      bulkUi,
      rowIndexById,
      colIndexById,
      rowIds,
      colIds,
      remoteClaims,
      lockOverrides,
    ],
  );

  /** F-16 TSV: text from Sheets, Excel, or anywhere — values only. */
  const applyPastedText = useCallback(
    (text: string) =>
      applyPaste<string>({
        matrix: parseTsv(text),
        write: (writes) =>
          session.pasteCells(
            sheetId,
            writes.map(({ rowId, colId, item }) => ({
              rowId,
              colId,
              value: item === "" ? null : item,
            })),
          ),
      }),
    [applyPaste, session, sheetId],
  );

  /**
   * F-16 (2026-10): a block copied from a Kola grid — this board or another —
   * lands exactly as it was copied: every cell's value, format, status, marks
   * and line kinds, plus the images contained in its cells.
   */
  const applyPastedCells = useCallback(
    (clipboard: CellClipboard) =>
      applyPaste<CopiedCell | null>({
        matrix: clipboard.rows,
        write: (writes) =>
          session.pasteCellCopies(
            sheetId,
            writes.map(({ rowId, colId, item }) => ({ rowId, colId, cell: item })),
          ),
        afterCells: ({ rowIds: targetRows, colIds: targetCols, startRow, startCol, skipped }) => {
          // The block REPLACES what the target cells held, images included: a
          // cell's contained elements are its content (F-26), and stacking the
          // copies on top of them would show both. Cells a peer is editing
          // keep theirs, exactly as they kept their text.
          const covered = new Set<string>();
          for (const rowId of targetRows) {
            for (const colId of targetCols) {
              const key = cellKey(rowId, colId);
              if (!skipped.has(key)) covered.add(key);
            }
          }
          const stale: string[] = [];
          for (const el of board.listElements()) {
            if (el.anchor.kind !== "cell") continue;
            if (covered.has(cellKey(el.anchor.rowId, el.anchor.colId))) stale.push(el.id);
          }
          session.deleteElements(stale);
          const duplicates = copiedElementDuplicates(clipboard.elements, (dr, dc) => {
            const rowId = targetRows[dr];
            const colId = targetCols[dc];
            if (rowId === undefined || colId === undefined) return null;
            if (skipped.has(cellKey(rowId, colId))) return null;
            // Rows/columns the paste just appended have no edge yet (the
            // layout follows the doc on the next render); the last edge is as
            // close as the element's fallback position cache needs to be.
            const x = GRID_ORIGIN.x + (colEdges[startCol + dc] ?? colEdges[colEdges.length - 1] ?? 0);
            const y = GRID_ORIGIN.y + (rowEdges[startRow + dr] ?? rowEdges[rowEdges.length - 1] ?? 0);
            return { rowId, colId, x, y };
          });
          session.duplicateElements(duplicates, []);
        },
      }),
    [applyPaste, board, session, sheetId, colEdges, rowEdges],
  );

  // document.oncopy / window.onpaste on the grid selection. Stable listeners
  // reading the latest closures through refs.
  const copyRef = useRef<(e: ClipboardEvent) => void>(() => {});
  const pasteRef = useRef<(e: ClipboardEvent) => void>(() => {});
  copyRef.current = (event: ClipboardEvent) => {
    if (editingRef.current !== null) return; // textarea keeps native copy
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)
    ) {
      return;
    }
    // A live DOM text selection (board title, toast, banner …) means the user
    // is copying THAT — native copy must win over the grid selection's TSV.
    if ((window.getSelection()?.toString() ?? "") !== "") return;
    const tsv = buildSelectionTsv();
    if (tsv === null || event.clipboardData === null || selRange === null) return;
    event.clipboardData.setData("text/plain", tsv);
    // The Kola flavor rides beside the TSV (features/board/cell-clipboard): a
    // Kola grid pastes the block exactly as it is here; Sheets, Excel and
    // every other reader see only the TSV.
    const block = buildCellClipboard(
      board,
      sheetId,
      rowIds.slice(selRange.r0, selRange.r1 + 1),
      colIds.slice(selRange.c0, selRange.c1 + 1),
    );
    event.clipboardData.setData(CELL_CLIPBOARD_MIME, serializeCellClipboard(block));
    event.preventDefault();
  };
  pasteRef.current = (event: ClipboardEvent) => {
    if (editingRef.current !== null) return; // typing surfaces keep native paste
    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
    ) {
      return;
    }
    const block = parseCellClipboard(event.clipboardData?.getData(CELL_CLIPBOARD_MIME) ?? "");
    if (block !== null) {
      event.preventDefault();
      void applyPastedCells(block);
      return;
    }
    const text = event.clipboardData?.getData("text/plain") ?? "";
    if (text.length === 0) return; // image pastes belong to ImageDropLayer
    event.preventDefault();
    void applyPastedText(text);
  };
  useEffect(() => {
    const onCopy = (e: ClipboardEvent) => copyRef.current(e);
    const onPaste = (e: ClipboardEvent) => pasteRef.current(e);
    document.addEventListener("copy", onCopy);
    window.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("copy", onCopy);
      window.removeEventListener("paste", onPaste);
    };
  }, []);

  /* ---------------- clearing / deleting ------------------------------ */

  const clearSelectedCells = useCallback(() => {
    if (frozen || selRange === null) return;
    const writes: { rowId: string; colId: string; value: null }[] = [];
    const claimed: { rowId: string; colId: string; value: null }[] = [];
    let claimantName: string | undefined;
    for (let r = selRange.r0; r <= selRange.r1; r++) {
      for (let c = selRange.c0; c <= selRange.c1; c++) {
        const rowId = rowIds[r];
        const colId = colIds[c];
        if (rowId === undefined || colId === undefined) continue;
        const snapshot = board.getCell(sheetId, rowId, colId);
        if (snapshot !== undefined && snapshot.v !== null) {
          const key = cellKey(rowId, colId);
          const claim = remoteClaims.get(key);
          if (claim !== undefined && !lockOverrides.has(key)) {
            // F-42: a bulk delete must not silently wipe a cell someone is
            // typing in — same block+override treatment as the editor path.
            claimed.push({ rowId, colId, value: null });
            claimantName = claim.user.name;
          } else {
            writes.push({ rowId, colId, value: null });
          }
        }
      }
    }
    if (writes.length > 0) session.pasteCells(sheetId, writes);
    if (claimed.length > 0) {
      showToast(skippedClaimMessage(claimed.length, claimantName), {
        actionLabel: "Clear anyway",
        onAction: () => session.pasteCells(sheetId, claimed),
      });
    }
  }, [board, session, sheetId, frozen, selRange, rowIds, colIds, remoteClaims, lockOverrides]);

  /* ---------------- keyboard (F-12/F-15/F-120) ----------------------- */

  /**
   * F-17-marks over a CELL selection: bold / strike every selected cell's
   * whole text. The editor's bar formats a character range because that is
   * what a writer inside a cell has highlighted; out here the gesture is a
   * range of cells, so the range each mark covers is the cell's own text.
   * Same marks, same keys, same data — the script view renders the result
   * without knowing which of the two wrote it.
   */
  const markSelectedCells = useCallback(
    (kind: "bold" | "strike") => {
      if (frozen || selRange === null) return;
      const cells: { rowId: string; colId: string }[] = [];
      for (let r = selRange.r0; r <= selRange.r1; r++) {
        for (let c = selRange.c0; c <= selRange.c1; c++) {
          const rowId = rowIds[r];
          const colId = colIds[c];
          if (rowId !== undefined && colId !== undefined) cells.push({ rowId, colId });
        }
      }
      session.toggleCellsMark(sheetId, cells, kind);
    },
    [session, sheetId, frozen, selRange, rowIds, colIds],
  );

  const onWrapperKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (editingRef.current !== null) return; // textarea handles its own keys
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest?.(".gt-pill-group, .grid-add-bubble") !== null
      ) {
        return;
      }
      // F-17-marks: ⌘B / ⌘⇧X over a cell selection. BEFORE the modifier
      // bail-out below, which is there so copy/paste/undo reach their own
      // handlers — these two chords are ours.
      const markKind = formatShortcut(event);
      if (markKind !== null && selRange !== null) {
        event.preventDefault();
        markSelectedCells(markKind);
        return;
      }
      if (event.metaKey || event.ctrlKey) return; // copy/paste/undo elsewhere
      const range = selRange;
      if (event.key === "Escape") {
        setSelection(null);
        return;
      }
      if (range === null) return;
      const { aRow, aCol } = range;
      const move = (dRow: number, dCol: number, extend: boolean) => {
        event.preventDefault();
        if (extend && selection !== null) {
          const hRow = rowIndexById.get(selection.head.rowId) ?? aRow;
          const hCol = colIndexById.get(selection.head.colId) ?? aCol;
          const rowId = rowIds[stepIndex(hRow, dRow, rowIds.length)];
          const colId = colIds[stepIndex(hCol, dCol, colIds.length)];
          if (rowId !== undefined && colId !== undefined) {
            setSelection({ anchor: selection.anchor, head: { rowId, colId } });
          }
          return;
        }
        selectCellAt(stepIndex(aRow, dRow, rowIds.length), stepIndex(aCol, dCol, colIds.length));
      };
      switch (event.key) {
        case "ArrowUp":
          move(-1, 0, event.shiftKey);
          return;
        case "ArrowDown":
          move(1, 0, event.shiftKey);
          return;
        case "ArrowLeft":
          move(0, -1, event.shiftKey);
          return;
        case "ArrowRight":
          move(0, 1, event.shiftKey);
          return;
        case "Tab":
          move(0, event.shiftKey ? -1 : 1, false);
          return;
        case "Enter":
        case "F2": {
          event.preventDefault();
          const rowId = rowIds[aRow];
          const colId = colIds[aCol];
          if (rowId !== undefined && colId !== undefined) openEditor(rowId, colId, "caret");
          return;
        }
        case "Delete":
        case "Backspace":
          event.preventDefault();
          clearSelectedCells();
          return;
        default: {
          // Typing on a selected cell starts editing, replacing content
          // (Sheets muscle memory). Space is reserved for canvas panning.
          if (event.key.length === 1 && !event.altKey && event.key !== " ") {
            event.preventDefault();
            const rowId = rowIds[aRow];
            const colId = colIds[aCol];
            if (rowId !== undefined && colId !== undefined) {
              openEditor(rowId, colId, "replace", event.key);
            }
          }
        }
      }
    },
    [
      selRange,
      selection,
      rowIds,
      colIds,
      rowIndexById,
      colIndexById,
      setSelection,
      selectCellAt,
      openEditor,
      clearSelectedCells,
      markSelectedCells,
    ],
  );

  const onEditorKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      event.stopPropagation(); // never reaches the wrapper's navigation keys
      const edit = editingRef.current;
      if (edit === null) return;
      // An IME candidate popup is open: Enter picks a character, Esc cancels
      // the composition, the arrows walk the list. Committing the cell and
      // moving down on the Enter that chose a Chinese character is the same
      // bug the script view had (core/keyboard.ts).
      if (isImeComposing(event.nativeEvent)) return;
      if (isImeEnterEcho(event.key, performance.now() - compositionEndedAt.current)) {
        event.preventDefault();
        return;
      }
      // F-17-type: while the `/` menu is open it OWNS these keys — Enter takes
      // the armed kind instead of committing a half-written line, and Esc
      // closes the menu instead of throwing the edit away. Placed ahead of
      // everything below for exactly that reason.
      if (typeMenuOpenRef.current) {
        const menu = typeMenuRef.current;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setTypeMenuActive((i) => (i + 1) % menu.options.length);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setTypeMenuActive((i) => (i - 1 + menu.options.length) % menu.options.length);
          return;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          const option = menu.options[menu.index];
          if (option !== undefined) pickTextTypeRef.current(option);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          // Close the menu ONLY: the writer is dismissing a command they did
          // not mean to start, not abandoning the line they are typing.
          setEditorCaret(null);
          return;
        }
      }
      // F-17-marks, the two toggles everybody already has in their fingers.
      // They act on the SELECTION, so with a bare caret they fall through to
      // the browser (⌘B does nothing in a textarea anyway) rather than
      // swallowing a key the writer may have meant for something else.
      if (event.currentTarget.selectionStart !== event.currentTarget.selectionEnd) {
        const kind = formatShortcut(event);
        if (kind !== null) {
          event.preventDefault();
          toggleEditorMark(kind);
          return;
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closeEditor(); // cancel — no write
        return;
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        commitAndMove(1, 0);
        return;
      }
      if (event.key === "Tab") {
        event.preventDefault();
        commitAndMove(0, event.shiftKey ? -1 : 1);
        return;
      }
      if (edit.mode === "replace" && !event.shiftKey) {
        // Quick-entry mode: arrows commit & navigate (Sheets).
        if (event.key === "ArrowUp") {
          event.preventDefault();
          commitAndMove(-1, 0);
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          commitAndMove(1, 0);
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          commitAndMove(0, -1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          commitAndMove(0, 1);
        }
      }
    },
    [closeEditor, commitAndMove, toggleEditorMark],
  );

  /* ---------------- cell pointer interactions (F-15) ----------------- */

  /** Wrapper-relative pointer position in BOARD units. */
  const boardPointFromClient = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const wrap = wrapRef.current;
      if (!wrap) return null;
      const rect = wrap.getBoundingClientRect();
      return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
    },
    [zoom],
  );

  /** Wrapper-relative board box of a cell — null when its edges aren't in. */
  const cellBoxOf = useCallback(
    (cell: CellIds): CellBox | null => {
      const r = rowIndexById.get(cell.rowId);
      const c = colIndexById.get(cell.colId);
      if (r === undefined || c === undefined) return null;
      const left = colEdges[c];
      const right = colEdges[c + 1];
      const top = rowEdges[r];
      const bottom = rowEdges[r + 1];
      if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
        return null;
      }
      return { left, top, right, bottom };
    },
    [rowIndexById, colIndexById, colEdges, rowEdges],
  );

  const cellFromEventTarget = (target: EventTarget | null): CellIds | null => {
    const el = (target as HTMLElement | null)?.closest?.("[data-r]") as HTMLElement | null;
    if (!el || el.dataset["r"] === undefined || el.dataset["c"] === undefined) return null;
    return { rowId: el.dataset["r"], colId: el.dataset["c"] };
  };

  const cancelLongPress = useCallback(() => {
    if (longPressRef.current !== null) {
      window.clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  /**
   * Clicking empty space within the table's full extent — the gutters, or
   * the frame background beyond the last mounted row/column (F-10: the
   * wrapper is sized to the table's FULL extent, but `.grid-table` only
   * covers the currently mounted rows) — never lands inside any `[data-r]`
   * cell, so onTablePointerDown below (bound to `.grid-table`) doesn't even
   * fire there, leaving a stale cell selection on screen. Bound to the outer
   * wrapper instead so it also catches clicks that miss `.grid-table`
   * entirely; every interactive control inside it (resize handles,
   * selection-extend corners, column/row pills) calls stopPropagation() on
   * press, so real interactions never reach here.
   */
  const onWrapperPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      if (cellFromEventTarget(event.target) === null) setSelection(null);
    },
    [setSelection],
  );

  /** Remember where the pointer was for the F-27 anchor (see `selectPointerRef`). */
  const recordSelectPointer = useCallback(
    (event: React.PointerEvent) => {
      if (event.type === "pointerdown" && event.button !== 0) return;
      const pt = boardPointFromClient(event.clientX, event.clientY);
      if (pt !== null) selectPointerRef.current = pt;
    },
    [boardPointFromClient],
  );

  const onWrapperPointerDownCapture = useCallback(
    (event: React.PointerEvent) => {
      recordSelectPointer(event);
      onGridPointerDown?.();
    },
    [recordSelectPointer, onGridPointerDown],
  );

  const forgetSelectPointer = useCallback(() => {
    selectPointerRef.current = null;
  }, []);

  const onTablePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      const cell = cellFromEventTarget(event.target);
      if (cell === null) return;
      if (event.pointerType === "touch") {
        // MOBILE CONTROLS (deliberately not desktop's): a one-finger drag on
        // the table PANS the board — selection happens on tap-UP, editing on
        // double-tap, the menu on long-press. preventDefault suppresses the
        // compatibility mouse events, so hover affordances and dblclick can
        // never phantom-fire from a tap.
        event.preventDefault();
        touchNavRef.current = {
          pointerId: event.pointerId,
          startClientX: event.clientX,
          startClientY: event.clientY,
          lastClientX: event.clientX,
          lastClientY: event.clientY,
          cell,
          mode: "pending",
        };
        safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
        cancelLongPress();
        const { clientX, clientY } = event;
        longPressRef.current = window.setTimeout(() => {
          longPressRef.current = null;
          const nav = touchNavRef.current;
          if (nav === null || nav.mode !== "pending") return;
          // The press became a menu — the coming pointerup is not a tap.
          touchNavRef.current = null;
          openCellMenuAtRef.current(cell, clientX, clientY);
        }, LONG_PRESS_MS);
        return;
      }
      wrapRef.current?.focus({ preventScroll: true });
      const edit = editingRef.current;
      if (edit !== null) {
        if (edit.rowId === cell.rowId && edit.colId === cell.colId) return; // clicking the edited cell
        commitEditor(); // Sheets: clicking elsewhere commits
      }
      if (event.shiftKey && selection !== null) {
        setSelection({ anchor: selection.anchor, head: cell });
        return;
      }
      setSelection({ anchor: cell, head: cell });
      // The gesture is UNDECIDED here: staying inside `cell` makes it an item
      // box, leaving it makes it the F-15 cell-range drag (see `CellMarquee`).
      selectDragRef.current = {
        pointerId: event.pointerId,
        cell,
        start: boardPointFromClient(event.clientX, event.clientY),
        box: cellBoxOf(cell),
        last: null,
        mode: "pending",
      };
      setIsSelecting(true);
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
    },
    [
      selection,
      setSelection,
      commitEditor,
      cancelLongPress,
      boardPointFromClient,
      cellBoxOf,
    ],
  );

  const onTablePointerMove = useCallback(
    (event: React.PointerEvent) => {
      const nav = touchNavRef.current;
      if (nav !== null && nav.pointerId === event.pointerId) {
        if (canvas.getState().pinching) {
          // A second finger landed — the camera gesture owns this pointer now.
          touchNavRef.current = null;
          cancelLongPress();
          return;
        }
        const dx = event.clientX - nav.lastClientX;
        const dy = event.clientY - nav.lastClientY;
        if (nav.mode === "pending") {
          if (
            Math.abs(event.clientX - nav.startClientX) < TOUCH_SLOP_SCREEN_PX &&
            Math.abs(event.clientY - nav.startClientY) < TOUCH_SLOP_SCREEN_PX
          ) {
            return; // still a tap
          }
          nav.mode = "pan";
          cancelLongPress();
        }
        nav.lastClientX = event.clientX;
        nav.lastClientY = event.clientY;
        canvas.getState().panBy(dx, dy);
        return;
      }
      const drag = selectDragRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      // F-34 in-cell item box. Containment is tested against the cell's own
      // board rect, NOT elementFromPoint: an image parked in the cell would
      // otherwise read as "no cell under the pointer" and the gesture could
      // never be decided. Once the drag has left, it is a cell range for good.
      if (drag.mode !== "cells" && drag.start !== null && drag.box !== null) {
        const pt = boardPointFromClient(event.clientX, event.clientY);
        if (pt !== null && withinCell(drag.box, pt)) {
          drag.last = pt;
          if (drag.mode === "items" || isMarquee(drag.start, pt)) {
            drag.mode = "items";
            onCellMarquee?.(cellMarqueeOf(drag.cell, drag.start, pt));
          }
          return;
        }
        if (drag.mode === "items") onCellMarquee?.(null);
        drag.mode = "cells";
      }
      // Pointer capture retargets events, so resolve the cell geometrically.
      const el = document.elementFromPoint(event.clientX, event.clientY);
      const cell = cellFromEventTarget(el);
      if (cell === null || tableRef.current?.contains(el as Node) !== true) return;
      const prev = selectionRef.current;
      if (prev === null) return;
      if (prev.head.rowId === cell.rowId && prev.head.colId === cell.colId) return;
      setSelection({ anchor: prev.anchor, head: cell });
    },
    [setSelection, canvas, cancelLongPress, boardPointFromClient, onCellMarquee],
  );

  const onTablePointerUp = useCallback(
    (event: React.PointerEvent) => {
      const nav = touchNavRef.current;
      if (nav !== null && nav.pointerId === event.pointerId) {
        touchNavRef.current = null;
        cancelLongPress();
        if (nav.mode !== "pending" || event.type === "pointercancel") return;
        // Tap: select the cell; a second tap on the same cell edits it.
        const cell = nav.cell;
        wrapRef.current?.focus({ preventScroll: true });
        const edit = editingRef.current;
        if (edit !== null) {
          if (edit.rowId === cell.rowId && edit.colId === cell.colId) return; // tapping the edited cell
          commitEditor(); // Sheets: tapping elsewhere commits
        }
        const now = performance.now();
        const last = lastTapRef.current;
        lastTapRef.current = { time: now, rowId: cell.rowId, colId: cell.colId };
        if (
          last !== null &&
          now - last.time < DOUBLE_TAP_MS &&
          last.rowId === cell.rowId &&
          last.colId === cell.colId
        ) {
          lastTapRef.current = null; // a triple-tap is not two double-taps
          openEditor(cell.rowId, cell.colId, "caret");
          return;
        }
        setSelection({ anchor: cell, head: cell });
        return;
      }
      const drag = selectDragRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      selectDragRef.current = null;
      setIsSelecting(false);
      if (drag.mode !== "items") return;
      onCellMarquee?.(null);
      // A stolen or cancelled pointer selects nothing — same rule as the
      // background marquee's `cancelMarquee`.
      if (event.type === "pointercancel") return;
      // Commit with the last point known to be INSIDE the cell: a pointerup
      // that lands a hair outside must not throw the box away.
      const end = boardPointFromClient(event.clientX, event.clientY);
      const to = end !== null && drag.box !== null && withinCell(drag.box, end) ? end : drag.last;
      if (drag.start === null || to === null) return;
      onCellMarqueeCommit?.(cellMarqueeOf(drag.cell, drag.start, to));
    },
    [
      setSelection,
      commitEditor,
      openEditor,
      cancelLongPress,
      boardPointFromClient,
      onCellMarquee,
      onCellMarqueeCommit,
    ],
  );

  const onTableDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      // F-12 double-click-to-edit. `event.target` is USELESS here: the first
      // click of the pair captured the pointer for the F-15 drag-select, and
      // capture retargets the compatibility mouse events — pointerup, click
      // and dblclick all arrive addressed to `.grid-table` itself, never to
      // the cell under the cursor, so reading the target found no cell and the
      // editor silently never opened. Resolve it geometrically, exactly as the
      // drag path above already does.
      const cell = cellFromEventTarget(document.elementFromPoint(event.clientX, event.clientY));
      if (cell !== null) openEditor(cell.rowId, cell.colId, "caret");
    },
    [openEditor],
  );

  /* ---------------- row/column ops + menus (F-13/F-12b) -------------- */

  // Menu items capture the STABLE id of the clicked row/column and re-resolve
  // its index at CLICK time — the menu can sit open while remote structural
  // changes land, and an index captured at open time would insert next to the
  // wrong neighbor (same C4-style guard as the F-13 add bubbles).
  const columnMenuItems = useCallback(
    (colIndex: number): MenuItem[] => {
      const colId = colIds[colIndex];
      if (colId === undefined) return [];
      const currentIndex = () => board.sortedColIds(sheetId).indexOf(colId);
      // F-28: re-read the lock at BUILD time from the doc, not from the
      // captured colInfos — same C4-style discipline as the index re-resolve.
      const locked = board.isColumnLocked(sheetId, colId);
      const items: MenuItem[] = [];

      // F-28: no rename affordance on the locked source column. Omitted rather
      // than greyed: "Original Script" is not a name the user picked and might
      // restore — it is the column's identity (F-70 keys the whole translation
      // UX on it), so offering the action at all misrepresents the column.
      if (!locked) {
        items.push({ label: "Rename column…", onClick: () => setRenaming(colId) });
      }
      // F-28: no "Insert column left" ON the source column — there is no slot
      // to its left. Omitted rather than clamped: an item that says "left" and
      // inserts right is a lie the user can't diagnose.
      if (canInsertColumnBefore(colIndex, sourceColIndex)) {
        items.push({
          label: "Insert column left",
          onClick: () => {
            const index = currentIndex();
            if (index >= 0 && canInsertColumnBefore(index, sourceColIndex)) {
              session.insertColumn(sheetId, index);
            }
          },
        });
      }
      items.push({
        label: "Insert column right",
        onClick: () => {
          const index = currentIndex();
          if (index >= 0) session.insertColumn(sheetId, index + 1);
        },
      });
      items.push({
        label: "Duplicate column",
        onClick: () => session.duplicateColumn(sheetId, colId),
      });
      items.push({
        // F-28: greyed, never hidden — the row's "Delete row" sits in the same
        // menu, so an item silently missing would read as a bug. The title
        // says why. `deleteColumn` refuses it regardless: this is affordance,
        // not enforcement.
        label: "Delete column",
        danger: true,
        separatorBefore: true,
        ...(locked
          ? {
              disabled: true,
              title: "The Original Script column is the translation source and can't be deleted",
            }
          : {}),
        onClick: () => session.deleteColumn(sheetId, colId),
      });
      return items;
    },
    [board, session, sheetId, colIds, sourceColIndex],
  );

  const rowMenuItems = useCallback(
    (rowIndex: number): MenuItem[] => {
      const rowId = rowIds[rowIndex];
      if (rowId === undefined) return [];
      const currentIndex = () => board.sortedRowIds(sheetId).indexOf(rowId);
      // Same full-track group detection as resizeGroupIds (F-14): if the
      // right-clicked row sits inside a whole-row multi-selection, "Delete
      // row" acts on every selected row in one step instead of just this one.
      const range = selRange;
      const selectedRowIds =
        range !== null &&
        range.r1 > range.r0 &&
        range.c0 === 0 &&
        range.c1 === colIds.length - 1 &&
        rowIndex >= range.r0 &&
        rowIndex <= range.r1
          ? rowIds.slice(range.r0, range.r1 + 1)
          : [rowId];
      return [
        {
          label: "Insert row above",
          onClick: () => {
            const index = currentIndex();
            if (index >= 0) session.insertRow(sheetId, index);
          },
        },
        {
          label: "Insert row below",
          onClick: () => {
            const index = currentIndex();
            if (index >= 0) session.insertRow(sheetId, index + 1);
          },
        },
        { label: "Duplicate row", onClick: () => session.duplicateRow(sheetId, rowId) },
        {
          label: selectedRowIds.length > 1 ? `Delete ${selectedRowIds.length} rows` : "Delete row",
          danger: true,
          separatorBefore: true,
          onClick: () =>
            selectedRowIds.length > 1
              ? session.deleteRows(sheetId, selectedRowIds)
              : session.deleteRow(sheetId, rowId),
        },
      ];
    },
    [board, session, sheetId, rowIds, colIds, selRange],
  );

  // Shared by desktop right-click (onTableContextMenu) and the mobile
  // long-press (the touch pointer handlers below) — the ONE cell-menu opener.
  const openCellMenuAt = useCallback(
    (cell: CellIds, clientX: number, clientY: number) => {
      if (frozen) return;
      const rowIndex = rowIndexById.get(cell.rowId);
      const colIndex = colIndexById.get(cell.colId);
      if (rowIndex === undefined || colIndex === undefined) return;
      // F-50 second entry path: right-click any cell → Add comment. Routed
      // through the same registry hook as the F-27 toolbar button; the cell's
      // rect is re-resolved by STABLE id at CLICK time (the menu can sit open
      // across remote structural changes — same C4-style guard as the other
      // menu items).
      const addCommentItem: MenuItem = {
        label: "Add comment",
        onClick: () => {
          const r = rowIndexById.get(cell.rowId);
          const c = colIndexById.get(cell.colId);
          if (r === undefined || c === undefined) return; // target deleted meanwhile
          const left = colEdges[c];
          const right = colEdges[c + 1];
          const top = rowEdges[r];
          const bottom = rowEdges[r + 1];
          if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
            return;
          }
          requestAddComment(session, {
            kind: "cell",
            sheetId,
            rowId: cell.rowId,
            colId: cell.colId,
            rect: {
              x: GRID_ORIGIN.x + left,
              y: GRID_ORIGIN.y + top,
              w: right - left,
              h: bottom - top,
            },
          });
        },
      };
      // F-123: a link that opens the board panned and zoomed onto THIS cell,
      // selected — the same link the toolbar's "Copy link" button copies.
      const copyLinkItem: MenuItem = {
        label: "Copy link to cell",
        title: "A link that opens this board on this cell",
        onClick: () => {
          void copyToClipboard(cellShareLink(session.boardId, cell.rowId, cell.colId)).then(
            (copied) => showToast(copied ? "Link to this cell copied" : "Couldn't reach the clipboard"),
          );
        },
      };
      const rowItems = rowMenuItems(rowIndex);
      const colItems = columnMenuItems(colIndex);
      const firstRowItem = rowItems[0];
      if (firstRowItem !== undefined) firstRowItem.separatorBefore = true;
      const firstColItem = colItems[0];
      if (firstColItem !== undefined && rowItems.length > 0) firstColItem.separatorBefore = true;
      const items = [addCommentItem, copyLinkItem, ...rowItems, ...colItems];
      setMenu({ x: clientX, y: clientY, items });
    },
    [
      frozen,
      rowIndexById,
      colIndexById,
      rowMenuItems,
      columnMenuItems,
      session,
      sheetId,
      colEdges,
      rowEdges,
    ],
  );

  openCellMenuAtRef.current = openCellMenuAt;

  const onTableContextMenu = useCallback(
    (event: React.MouseEvent) => {
      const cell = cellFromEventTarget(event.target);
      if (cell === null || frozen) return;
      event.preventDefault();
      openCellMenuAt(cell, event.clientX, event.clientY);
    },
    [frozen, openCellMenuAt],
  );

  /* ---------------- hover handles (F-12b) + add hints (F-13) --------- */

  /* -------- touch selection-extend handles (mobile range select) ----- */

  // With one-finger table drags panning the board, touch users need another
  // way to select a RANGE: Sheets-style draggable dots on the selection's
  // top-left/bottom-right corners (rendered only for coarse pointers — CSS).
  const beginSelHandleDrag = useCallback(
    (event: React.PointerEvent, corner: "tl" | "br") => {
      if (frozen || event.button !== 0) return;
      const range = selRange;
      if (range === null) return;
      event.preventDefault();
      event.stopPropagation(); // never also a table tap/pan
      // Dragging a corner extends from the FIXED opposite corner.
      const anchorRowId = rowIds[corner === "tl" ? range.r1 : range.r0];
      const anchorColId = colIds[corner === "tl" ? range.c1 : range.c0];
      if (anchorRowId === undefined || anchorColId === undefined) return;
      selHandleRef.current = {
        pointerId: event.pointerId,
        anchor: { rowId: anchorRowId, colId: anchorColId },
      };
      setIsSelecting(true);
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
    },
    [frozen, selRange, rowIds, colIds],
  );

  const onSelHandleMove = useCallback(
    (event: React.PointerEvent) => {
      const drag = selHandleRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (canvas.getState().pinching) {
        selHandleRef.current = null;
        setIsSelecting(false);
        return;
      }
      const pt = boardPointFromClient(event.clientX, event.clientY);
      if (pt === null) return;
      // Clamp into the table so dragging past an edge selects boundary cells.
      const clamp = (edges: readonly number[], pos: number): number => {
        const lo = edges[0];
        const hi = edges[edges.length - 1];
        return lo === undefined || hi === undefined ? pos : Math.min(Math.max(pos, lo), hi - 0.5);
      };
      const r = indexAtPosition(rowEdges, clamp(rowEdges, pt.y));
      const c = indexAtPosition(colEdges, clamp(colEdges, pt.x));
      if (r < 0 || c < 0) return;
      const rowId = rowIds[r];
      const colId = colIds[c];
      if (rowId === undefined || colId === undefined) return;
      const prev = selectionRef.current;
      if (prev !== null && prev.head.rowId === rowId && prev.head.colId === colId) return;
      setSelection({ anchor: drag.anchor, head: { rowId, colId } });
    },
    [canvas, boardPointFromClient, rowEdges, colEdges, rowIds, colIds, setSelection],
  );

  const endSelHandleDrag = useCallback((event: React.PointerEvent) => {
    const drag = selHandleRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    selHandleRef.current = null;
    setIsSelecting(false);
  }, []);

  const onWrapperMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (frozen || pillDragRef.current !== null || resizeRef.current !== null) return;
      // Hovering an affordance must not re-evaluate (and hide) it.
      const target = event.target as HTMLElement;
      if (target.closest?.(".gt-pill-group, .grid-add-bubble, .gt-resize")) return;
      const pt = boardPointFromClient(event.clientX, event.clientY);
      if (pt === null) return;
      const gutter = GUTTER_SCREEN_PX * inv;
      const tol = BOUNDARY_TOLERANCE_SCREEN_PX * inv;
      let nextHover: HoverHandle | null = null;
      let nextHint: AddHint | null = null;
      if (pt.y < TABLE_BORDER && pt.y >= -gutter && pt.x >= -tol && pt.x <= tableW + tol) {
        const b = nearestBoundary(colEdges, pt.x, tol);
        // F-28: no add bubble on the boundary LEFT of the locked source column
        // — there is no insertable slot there, and a bubble that inserts one
        // column over would land visibly away from where it was clicked.
        if (b !== null && canInsertColumnBefore(b, sourceColIndex)) {
          nextHint = { kind: "col", beforeId: colIds[b], x: colEdges[b] ?? 0, y: -gutter / 2 };
        } else if (b === null) {
          const c = indexAtPosition(colEdges, pt.x);
          if (c >= 0) nextHover = { kind: "col", index: c };
        }
      } else if (pt.x < TABLE_BORDER && pt.x >= -gutter && pt.y >= -tol && pt.y <= tableH + tol) {
        const b = nearestBoundary(rowEdges, pt.y, tol);
        if (b !== null) {
          nextHint = { kind: "row", beforeId: rowIds[b], x: -gutter / 2, y: rowEdges[b] ?? 0 };
        } else {
          const r = indexAtPosition(rowEdges, pt.y);
          if (r >= 0) nextHover = { kind: "row", index: r };
        }
      }
      setHover((prev) =>
        prev?.kind === nextHover?.kind && prev?.index === nextHover?.index ? prev : nextHover,
      );
      setAddHint((prev) =>
        prev?.kind === nextHint?.kind &&
        prev?.beforeId === nextHint?.beforeId &&
        prev?.x === nextHint?.x &&
        prev?.y === nextHint?.y
          ? prev
          : nextHint,
      );
    },
    [
      frozen,
      boardPointFromClient,
      inv,
      tableW,
      tableH,
      colEdges,
      rowEdges,
      colIds,
      rowIds,
      sourceColIndex,
    ],
  );

  const applyAddHint = useCallback(() => {
    if (addHint === null) return;
    setAddHint(null);
    // Re-resolve the captured neighbor id to its CURRENT index — a remote
    // insert/delete/move between hover and click must not shift where the
    // new row/column lands (C1 ord keys make indices resolve-time-only).
    const index =
      addHint.beforeId === undefined
        ? addHint.kind === "col"
          ? colIds.length
          : rowIds.length
        : addHint.kind === "col"
          ? colIndexById.get(addHint.beforeId)
          : rowIndexById.get(addHint.beforeId);
    if (index === undefined) return; // neighbor deleted — boundary is gone
    if (addHint.kind === "col") {
      // F-28: re-checked at CLICK time like the index itself — a remote change
      // between hover and click could have made this the source's left edge.
      if (!canInsertColumnBefore(index, sourceColIndex)) return;
      session.insertColumn(sheetId, index);
    } else {
      session.insertRow(sheetId, index);
    }
  }, [
    addHint,
    session,
    sheetId,
    colIds.length,
    rowIds.length,
    colIndexById,
    rowIndexById,
    sourceColIndex,
  ]);

  /* ---------------- pill drag-reorder (F-12b, single ord write) ------ */

  // Window-listener plumbing: the drag must survive its affordance element
  // unmounting mid-drag (see attachWindowPointerListeners).
  const pillMoveRef = useRef<(event: PointerEvent) => void>(() => {});
  const pillUpRef = useRef<(event: PointerEvent) => void>(() => {});
  const pillDetachRef = useRef<(() => void) | null>(null);
  const resizeMoveRef = useRef<(event: PointerEvent) => void>(() => {});
  const resizeUpRef = useRef<(event: PointerEvent) => void>(() => {});
  const resizeDetachRef = useRef<(() => void) | null>(null);

  const beginPillDrag = useCallback(
    (event: React.PointerEvent, kind: "col" | "row", id: string, from: number) => {
      if (event.button !== 0 || frozen) return;
      event.preventDefault();
      event.stopPropagation();
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
      pillDragRef.current = {
        kind,
        id,
        from,
        // F-28: the locked source column cannot be reordered (`moveColumn`
        // refuses it). The press still becomes a pill press — click-to-select
        // and the ⋮ menu keep working — but it can never become a REORDER
        // drag: `reorderable` false makes onPillPointerMove bail before it
        // sets `moved`, so no drop indicator appears and the drop commits
        // nothing. That IS the "refused with a subtle cue": the column simply
        // does not lift, and the pill advertises why via its cursor + title.
        reorderable: !(kind === "col" && board.isColumnLocked(sheetId, id)),
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        moved: false,
        boundary: from,
      };
      pillDetachRef.current?.();
      pillDetachRef.current = attachWindowPointerListeners(pillMoveRef, pillUpRef);
    },
    [frozen, board, sheetId],
  );

  /** A two-finger camera gesture stole the drag's pointer — abort the pill
   * drag with no reorder commit (the fingers were panning, not dropping). */
  const abortPillDragIfPinching = useCallback((): boolean => {
    if (!canvas.getState().pinching) return false;
    pillDetachRef.current?.();
    pillDetachRef.current = null;
    pillDragRef.current = null;
    setDragIndicator(null);
    return true;
  }, [canvas]);

  const onPillPointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = pillDragRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (abortPillDragIfPinching()) return;
      if (!drag.reorderable) return; // F-28: the locked column never lifts
      if (
        !drag.moved &&
        Math.abs(event.clientX - drag.startClientX) < DRAG_THRESHOLD_SCREEN_PX &&
        Math.abs(event.clientY - drag.startClientY) < DRAG_THRESHOLD_SCREEN_PX
      ) {
        return;
      }
      drag.moved = true;
      const pt = boardPointFromClient(event.clientX, event.clientY);
      if (pt === null) return;
      const edges = drag.kind === "col" ? colEdges : rowEdges;
      const pos = drag.kind === "col" ? pt.x : pt.y;
      // The boundary depends only on the pointer position; the vacated-slot
      // adjustment happens at drop time against the CURRENT index.
      const { boundary } = reorderTarget(edges, drag.from, pos);
      // F-28 (half 2): another column may not land at/before the locked source
      // column — `moveColumn` refuses it, so the DROP LINE must never offer it.
      // Clamping (rather than hiding the indicator) keeps the drag alive and
      // shows the nearest legal home: dragging left past the source parks the
      // line at its right edge and stays there. That is the subtle cue — the
      // line simply will not cross.
      const clamped =
        drag.kind === "col"
          ? clampReorderBoundary(boundary, columnReorderBounds(colIds.length, sourceColIndex))
          : boundary;
      drag.boundary = clamped;
      setDragIndicator((prev) =>
        prev?.kind === drag.kind && prev?.boundary === clamped
          ? prev
          : { kind: drag.kind, boundary: clamped },
      );
    },
    [boardPointFromClient, colEdges, rowEdges, colIds.length, sourceColIndex, abortPillDragIfPinching],
  );
  pillMoveRef.current = onPillPointerMove;

  const onPillPointerUp = useCallback(
    (event: PointerEvent) => {
      const drag = pillDragRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (abortPillDragIfPinching()) return;
      pillDetachRef.current?.();
      pillDetachRef.current = null;
      pillDragRef.current = null;
      setDragIndicator(null);
      if (!drag.moved) {
        // Click = select the whole column/row; Shift+click extends the
        // full-track selection from the current anchor (F-14 multi-resize).
        const prev = selectionRef.current;
        if (drag.kind === "col") {
          const first = rowIds[0];
          const last = rowIds[rowIds.length - 1];
          if (first !== undefined && last !== undefined) {
            const anchorColId =
              event.shiftKey && prev !== null ? prev.anchor.colId : drag.id;
            setSelection({
              anchor: { rowId: first, colId: anchorColId },
              head: { rowId: last, colId: drag.id },
            });
          }
        } else {
          const first = colIds[0];
          const last = colIds[colIds.length - 1];
          if (first !== undefined && last !== undefined) {
            const anchorRowId =
              event.shiftKey && prev !== null ? prev.anchor.rowId : drag.id;
            setSelection({
              anchor: { rowId: anchorRowId, colId: first },
              head: { rowId: drag.id, colId: last },
            });
          }
        }
        wrapRef.current?.focus({ preventScroll: true });
        return;
      }
      // Re-resolve the dragged id's CURRENT index at drop time — a remote
      // move of the same row/column mid-drag would make the drag-start index
      // land the drop one slot off the indicator (C4 discipline; the id
      // being gone means it was deleted → drop is a no-op).
      const from =
        drag.kind === "col" ? colIndexById.get(drag.id) : rowIndexById.get(drag.id);
      if (from === undefined) return;
      const moveIndex = drag.boundary > from ? drag.boundary - 1 : drag.boundary;
      if (moveIndex !== from) {
        // Single fractional-ord write (docs/07 C1) — never delete+reinsert.
        if (drag.kind === "col") session.moveColumn(sheetId, drag.id, moveIndex);
        else session.moveRow(sheetId, drag.id, moveIndex);
      }
    },
    [session, sheetId, rowIds, colIds, colIndexById, rowIndexById, setSelection, abortPillDragIfPinching],
  );
  pillUpRef.current = onPillPointerUp;

  /* ---------------- boundary drag-resize (F-14) ---------------------- */

  /** The tracks a resize/autofit on `id` applies to: every column/row of a
   * full-track multi-selection containing it, else just `id` itself (F-14
   * "multi-select columns then drag = resize all together"). */
  const resizeGroupIds = useCallback(
    (kind: "col" | "row", id: string): string[] => {
      const range = selRange;
      if (kind === "col") {
        if (
          range !== null &&
          range.c1 > range.c0 &&
          range.r0 === 0 &&
          range.r1 === rowIds.length - 1
        ) {
          const index = colIndexById.get(id);
          if (index !== undefined && index >= range.c0 && index <= range.c1) {
            return colIds.slice(range.c0, range.c1 + 1);
          }
        }
        return [id];
      }
      if (
        range !== null &&
        range.r1 > range.r0 &&
        range.c0 === 0 &&
        range.c1 === colIds.length - 1
      ) {
        const index = rowIndexById.get(id);
        if (index !== undefined && index >= range.r0 && index <= range.r1) {
          return rowIds.slice(range.r0, range.r1 + 1);
        }
      }
      return [id];
    },
    [selRange, rowIds, colIds, rowIndexById, colIndexById],
  );

  const beginResize = useCallback(
    (event: React.PointerEvent, kind: "col" | "row", id: string, base: number) => {
      if (event.button !== 0 || frozen) return;
      event.preventDefault();
      event.stopPropagation();
      safeSetPointerCapture(event.currentTarget as HTMLElement, event.pointerId);
      const ids = resizeGroupIds(kind, id);
      let elementFloor = 0;
      for (const trackId of ids) {
        const floor =
          kind === "col"
            ? colElementWidth(board, sheetId, trackId)
            : rowElementHeight(board, sheetId, trackId);
        if (floor > elementFloor) elementFloor = floor;
      }
      resizeRef.current = {
        kind,
        ids,
        pointerId: event.pointerId,
        startClient: kind === "col" ? event.clientX : event.clientY,
        base,
        last: base,
        elementFloor,
      };
      resizeDetachRef.current?.();
      resizeDetachRef.current = attachWindowPointerListeners(resizeMoveRef, resizeUpRef);
    },
    [frozen, resizeGroupIds, board, sheetId],
  );

  /** A two-finger camera gesture stole the drag's pointer — abort the resize
   * with no commit and restore the pre-drag sizes (the movement sampled
   * mid-gesture was the fingers panning, not a chosen size). */
  const abortResizeIfPinching = useCallback((): boolean => {
    const drag = resizeRef.current;
    if (drag === null || !canvas.getState().pinching) return false;
    resizeDetachRef.current?.();
    resizeDetachRef.current = null;
    resizeRef.current = null;
    const clear = (prev: ReadonlyMap<string, number>) => {
      const next = new Map(prev);
      for (const id of drag.ids) next.delete(id);
      return next;
    };
    if (drag.kind === "col") setWidthOverrides(clear);
    else setHeightOverrides(clear);
    return true;
  }, [canvas]);

  const onResizePointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = resizeRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (abortResizeIfPinching()) return;
      const client = drag.kind === "col" ? event.clientX : event.clientY;
      const raw = drag.base + (client - drag.startClient) / zoom;
      // The track can never shrink below its contained elements' extent
      // (F-26/F-36 "contained" model) — to make it smaller, move/resize the
      // elements inside it first.
      const next = Math.max(
        drag.kind === "col" ? clampManualColWidth(raw) : clampManualRowHeight(raw),
        drag.elementFloor,
      );
      if (next === drag.last) return;
      drag.last = next;
      // Live-local preview at drag rate — no sync spam (F-14). A multi-track
      // group previews every selected column/row at the dragged size.
      if (drag.kind === "col") {
        setWidthOverrides((prev) => {
          const map = new Map(prev);
          for (const id of drag.ids) map.set(id, next);
          return map;
        });
      } else {
        setHeightOverrides((prev) => {
          const map = new Map(prev);
          for (const id of drag.ids) map.set(id, next);
          return map;
        });
      }
    },
    [zoom, abortResizeIfPinching],
  );
  resizeMoveRef.current = onResizePointerMove;

  const onResizePointerUp = useCallback(
    (event: PointerEvent) => {
      const drag = resizeRef.current;
      if (drag === null || drag.pointerId !== event.pointerId) return;
      if (abortResizeIfPinching()) return;
      resizeDetachRef.current?.();
      resizeDetachRef.current = null;
      resizeRef.current = null;
      // Commit ONE transaction on release; the manual size is a MINIMUM for
      // auto-fit (F-26) — auto-widen/auto-grow may still render it larger.
      // A zero-movement click commits NOTHING: `base` is the EFFECTIVE
      // (possibly auto-grown) size — derived client state that must never be
      // frozen into the CRDT as a manual floor (docs/04 §5), and the spurious
      // LWW write would clobber a concurrent remote resize of the same key.
      const changed = drag.last !== drag.base;
      if (drag.kind === "col") {
        if (changed) {
          session.batch(() => {
            for (const id of drag.ids) session.setColumnWidth(sheetId, id, drag.last);
          });
        }
        setWidthOverrides((prev) => {
          const next = new Map(prev);
          for (const id of drag.ids) next.delete(id);
          return next;
        });
      } else {
        if (changed) {
          session.batch(() => {
            for (const id of drag.ids) session.setRowHeight(sheetId, id, drag.last);
          });
        }
        setHeightOverrides((prev) => {
          const next = new Map(prev);
          for (const id of drag.ids) next.delete(id);
          return next;
        });
      }
    },
    [session, sheetId, abortResizeIfPinching],
  );
  resizeUpRef.current = onResizePointerUp;

  // If the whole table unmounts mid-drag, detach listeners and drop state.
  useEffect(() => {
    return () => {
      pillDetachRef.current?.();
      pillDetachRef.current = null;
      resizeDetachRef.current?.();
      resizeDetachRef.current = null;
      pillDragRef.current = null;
      resizeRef.current = null;
      touchNavRef.current = null;
      selHandleRef.current = null;
      if (longPressRef.current !== null) {
        window.clearTimeout(longPressRef.current);
        longPressRef.current = null;
      }
    };
  }, []);

  /* ---------------- double-click autofit (F-14) ---------------------- */

  const autofitColumns = useCallback(
    (ids: readonly string[]) => {
      if (frozen) return;
      // Sheets behavior: the manual width becomes exactly what the widest
      // content line needs (clamped to the F-14 manual bounds). One
      // transaction = one undo step for a multi-column autofit.
      session.batch(() => {
        for (const colId of ids) {
          let content = 0;
          for (const rowId of rowIds) {
            const snapshot = board.getCell(sheetId, rowId, colId);
            if (snapshot === undefined || snapshot.v === null) continue;
            // Images are elements (F-30) — never autofit a column to an inline
            // image thumb; a stray legacy image value is skipped.
            if (isImageCellValue(snapshot.v)) continue;
            const w = maxLineWidth(cellPlainText(snapshot.v), measureTextWidth);
            if (w > content) content = w;
          }
          // Never autofit narrower than the column's contained elements
          // (F-26/F-36) — same floor as a manual drag-resize.
          const width = Math.max(
            autofitColumnWidth(content, DEFAULT_COLUMN_WIDTH),
            colElementWidth(board, sheetId, colId),
          );
          session.setColumnWidth(sheetId, colId, width);
        }
      });
    },
    [board, session, sheetId, frozen, rowIds, measureTextWidth],
  );

  const autofitRows = useCallback(
    (ids: readonly string[]) => {
      if (frozen) return;
      // F-26 makes row height content-derived; autofit = drop the manual
      // floor back to the default so content alone drives the height — but
      // never below the row's contained elements (F-26/F-36), same floor as
      // a manual drag-resize.
      session.batch(() => {
        for (const rowId of ids) {
          const height = Math.max(DEFAULT_ROW_HEIGHT, rowElementHeight(board, sheetId, rowId));
          session.setRowHeight(sheetId, rowId, height);
        }
      });
    },
    [board, session, sheetId, frozen],
  );

  /* ---------------- F-30 drop targeting (GridApi) -------------------- */

  const hitTestCell = useCallback(
    (clientX: number, clientY: number): CellHit | null => {
      const wrap = wrapRef.current;
      if (!wrap) return null;
      const rect = wrap.getBoundingClientRect();
      const hit = cellAtPosition(
        colEdges,
        rowEdges,
        (clientX - rect.left) / zoom,
        (clientY - rect.top) / zoom,
      );
      if (hit === null) return null;
      const rowId = rowIds[hit.row];
      const colId = colIds[hit.col];
      if (rowId === undefined || colId === undefined) return null;
      return {
        rowId,
        colId,
        rect: {
          left: rect.left + hit.rect.left * zoom,
          top: rect.top + hit.rect.top * zoom,
          width: hit.rect.width * zoom,
          height: hit.rect.height * zoom,
        },
      };
    },
    [colEdges, rowEdges, rowIds, colIds, zoom],
  );

  /**
   * The TOP-LEFT cell of the range, not its anchor: a range dragged from
   * bottom-right to top-left must still paste where a spreadsheet would put
   * it. `selRange` is already normalized and already reflects a row/column
   * deleted under the selection (it resolves through the index maps).
   */
  const selectedCell = useCallback((): SelectedCell | null => {
    const range = selRange;
    if (range === null) return null;
    const rowId = rowIds[range.r0];
    const colId = colIds[range.c0];
    const left = colEdges[range.c0];
    const top = rowEdges[range.r0];
    if (rowId === undefined || colId === undefined || left === undefined || top === undefined) {
      return null;
    }
    return { rowId, colId, x: GRID_ORIGIN.x + left, y: GRID_ORIGIN.y + top };
  }, [selRange, rowIds, colIds, colEdges, rowEdges]);

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, [setSelection]);

  const selectCellById = useCallback(
    (rowId: string, colId: string): boolean => {
      if (!rowIndexById.has(rowId) || !colIndexById.has(colId)) return false;
      const cell = { rowId, colId };
      setSelection({ anchor: cell, head: cell });
      return true;
    },
    [rowIndexById, colIndexById, setSelection],
  );

  const focusGrid = useCallback(() => {
    wrapRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.current = {
      hitTestCell,
      selectedCell,
      clearSelection,
      selectCell: selectCellById,
      focus: focusGrid,
    };
    return () => {
      gridApi.current = null;
    };
  }, [gridApi, hitTestCell, selectedCell, clearSelection, selectCellById, focusGrid]);

  /* ---------------- render ------------------------------------------ */

  const canEdit = !frozen;
  const gutter = GUTTER_SCREEN_PX * inv;
  // F-17-color: the two tokens the readable-ink derivation computes FROM. One
  // read per theme flip, shared by every mounted cell.
  const themeColors = useThemeColors();

  // Cells of the MOUNTED rows only (F-10 windowing), row-major — the
  // correction pass indexes children as (r - firstRow) * nCols + c.
  const cells: ReactNode[] = [];
  if (!windowEmpty) {
    for (let r = firstRow; r <= lastRow; r++) {
      const rowId = rowIds[r];
      if (rowId === undefined) continue;
      // The row's derived height is the cell's min-height. It already folds in
      // the stored floor (F-14 manual resize + F-36 element ratchet) and, being
      // ceil()ed, is >= the row's intrinsic height — so the row lands EXACTLY
      // on the predicted edge that pins, selection rects and presence
      // highlights resolve against (docs/10 §2c). `min-height` and never
      // `height`: an under-prediction (Thai/Khmer) must grow the row rather
      // than crop it (F-26), and the correction pass then heals the edge.
      const rowMinHeight = rowHeights[r] ?? DEFAULT_ROW_HEIGHT;
      for (let c = 0; c < colIds.length; c++) {
        const colId = colIds[c];
        if (colId === undefined) continue;
        const snapshot = board.getCell(sheetId, rowId, colId);
        const key = cellKey(rowId, colId);
        const claim = remoteClaims.get(key);
        // Images are ALWAYS elements now (F-30, inline image cell value
        // retired) — a cell never renders an inline image. Any element
        // contained in this cell paints in the ElementsLayer above the grid
        // (F-36). A stray legacy image value (only reachable before the server
        // migration) renders as empty rather than "[object Object]".
        const text = snapshot === undefined ? "" : cellPlainText(snapshot.v);
        // F-155: a Short's "Place video" cell — its script path is the row above.
        let placeVideoScript: string | undefined;
        if (isPlaceVideoCell(text)) {
          const aboveRowId = rowIds[r - 1];
          const above = aboveRowId === undefined ? undefined : board.getCell(sheetId, aboveRowId, colId);
          placeVideoScript = above === undefined ? "" : cellPlainText(above.v);
        }
        cells.push(
          <CellView
            key={key}
            rowId={rowId}
            colId={colId}
            text={text}
            minHeight={rowMinHeight}
            bg={snapshot?.bg}
            color={snapshot?.fmt?.color}
            surface={themeColors.surface}
            defaultText={themeColors.text}
            claimColor={claim?.user.color}
            status={snapshot?.st}
            isSource={c === sourceColIndex}
            marks={snapshot?.marks}
            types={snapshot?.ty}
            placeVideoScript={placeVideoScript}
          />,
        );
      }
    }
  }

  /** Where the mounted block sits in the wrapper (F-10): exactly the top edge
   *  of its first row, so the unmounted rows above it occupy their predicted
   *  extent with no DOM nodes. */
  const blockTop = windowEmpty ? TABLE_BORDER : (rowEdges[firstRow] ?? TABLE_BORDER);

  // Selection overlay rect (board units).
  const selectionRect = useMemo(() => {
    if (selRange === null) return null;
    const left = colEdges[selRange.c0];
    const right = colEdges[selRange.c1 + 1];
    const top = rowEdges[selRange.r0];
    const bottom = rowEdges[selRange.r1 + 1];
    if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
      return null;
    }
    return {
      left,
      top,
      width: right - left,
      height: bottom - top,
      multi: selRange.r1 > selRange.r0 || selRange.c1 > selRange.c0,
    };
  }, [selRange, colEdges, rowEdges]);

  /* ---------------- F-27 toolbar anchor ------------------------------ */

  // The pill hides during interactions: drag-select, pill drag-reorder
  // (indicator only exists once the drag actually moved), and F-14 resize
  // (override maps only populate once the drag actually moved) — all
  // reactive state, so the effect below re-fires exactly on those edges.
  const interacting =
    isSelecting ||
    dragIndicator !== null ||
    widthOverrides.size > 0 ||
    heightOverrides.size > 0;
  const isEditing = editing !== null;
  useEffect(() => {
    if (onToolbarAnchor === undefined) return;
    if (frozen || isEditing || interacting || selection === null || selRange === null || selectionRect === null) {
      onToolbarAnchor(null);
      return;
    }
    const toBoard = (left: number, top: number, w: number, h: number) => ({
      // The wrapper sits at GRID_ORIGIN; selection geometry is wrapper-
      // relative board units, so this is the full board-space rect.
      x: GRID_ORIGIN.x + left,
      y: GRID_ORIGIN.y + top,
      w,
      h,
    });
    const rect = toBoard(
      selectionRect.left,
      selectionRect.top,
      selectionRect.width,
      selectionRect.height,
    );
    // The anchor CELL's rect (comment pin target, docs/04 §3 cell anchor).
    const aLeft = colEdges[selRange.aCol];
    const aRight = colEdges[selRange.aCol + 1];
    const aTop = rowEdges[selRange.aRow];
    const aBottom = rowEdges[selRange.aRow + 1];
    const anchorRect =
      aLeft !== undefined && aRight !== undefined && aTop !== undefined && aBottom !== undefined
        ? toBoard(aLeft, aTop, aRight - aLeft, aBottom - aTop)
        : rect;
    // Where the hand was when this selection was made (wrapper-relative, like
    // the selection geometry), lifted into board space with the same origin.
    const pointer = selectPointerRef.current;
    onToolbarAnchor({
      kind: "cells",
      sheetId,
      rowIds: rowIds.slice(selRange.r0, selRange.r1 + 1),
      colIds: colIds.slice(selRange.c0, selRange.c1 + 1),
      anchor: { rowId: selection.anchor.rowId, colId: selection.anchor.colId, rect: anchorRect },
      rect,
      ...(pointer === null
        ? {}
        : { pointer: { x: GRID_ORIGIN.x + pointer.x, y: GRID_ORIGIN.y + pointer.y } }),
    });
  }, [
    onToolbarAnchor,
    frozen,
    isEditing,
    interacting,
    selection,
    selRange,
    selectionRect,
    colEdges,
    rowEdges,
    rowIds,
    colIds,
    sheetId,
  ]);

  // Unmount (sheet switch / board close) must never leave a stale pill up.
  useEffect(() => {
    return () => onToolbarAnchor?.(null);
  }, [onToolbarAnchor]);

  /* ---------------- F-52 layout publication (comment anchoring) ------ */

  // Every layout pass — structure, F-14 resizes, F-26 auto-grow.
  //
  // F-10: `rowEdges` covers EVERY row, not just the mounted ones, and is
  // derived (predicted + measured cache), not read back from the DOM. Both
  // halves are load-bearing:
  //   · ALL rows — docs/07 C4 degrades a comment/element anchored to an id
  //     that a PRESENT layout lacks. Publishing only the ~50 mounted rows
  //     would read as "9 950 rows were just deleted" and orphan every
  //     off-screen pin. This is the sharpest hazard in the windowing change.
  //   · not DOM-derived — an unmounted row has no node to measure, and the
  //     panel's jump-to-pin must land on a row 9 000 rows away without first
  //     mounting it.
  useEffect(() => {
    onLayout?.({
      sheetId,
      rowIds,
      colIds,
      rowIndex: rowIndexById,
      colIndex: colIndexById,
      colEdges,
      rowEdges,
      originX: GRID_ORIGIN.x,
      originY: GRID_ORIGIN.y,
    });
  }, [onLayout, sheetId, rowIds, colIds, rowIndexById, colIndexById, colEdges, rowEdges]);

  // Unmounting retracts the layout: "grid not mounted" must read as unknown
  // (no C4 orphaning), never as "all rows deleted".
  useEffect(() => {
    return () => onLayout?.(null);
  }, [onLayout]);

  // Remote presence overlays (F-41 selection fills, F-42 editing outlines).
  const peerOverlays = useMemo(() => {
    const out: ReactNode[] = [];
    const rectFor = (r0: number, c0: number, r1: number, c1: number) => {
      const left = colEdges[Math.min(c0, c1)];
      const right = colEdges[Math.max(c0, c1) + 1];
      const top = rowEdges[Math.min(r0, r1)];
      const bottom = rowEdges[Math.max(r0, r1) + 1];
      if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
        return null;
      }
      return { left, top, width: right - left, height: bottom - top };
    };
    for (const peer of peers) {
      const sel = peer.selection;
      if (sel && sel.sheetId === sheetId) {
        const rA = rowIndexById.get(sel.anchor.rowId);
        const cA = colIndexById.get(sel.anchor.colId);
        const rH = rowIndexById.get(sel.head.rowId);
        const cH = colIndexById.get(sel.head.colId);
        if (rA !== undefined && cA !== undefined && rH !== undefined && cH !== undefined) {
          const rect = rectFor(rA, cA, rH, cH);
          if (rect) {
            out.push(
              <div
                key={`sel-${peer.clientId}`}
                className="gt-peer-overlay"
                style={{
                  ...rect,
                  background: withAlpha(peer.user.color, 0.14),
                  border: `${1.5 * inv}px solid ${withAlpha(peer.user.color, 0.75)}`,
                }}
              />,
            );
          }
        }
      }
      const editingCell = peer.editingCell;
      if (editingCell && editingCell.sheetId === sheetId) {
        const r = rowIndexById.get(editingCell.rowId);
        const c = colIndexById.get(editingCell.colId);
        if (r !== undefined && c !== undefined) {
          const rect = rectFor(r, c, r, c);
          if (rect) {
            out.push(
              <div
                key={`edit-${peer.clientId}`}
                className="gt-peer-overlay"
                style={{ ...rect, border: `${2 * inv}px solid ${peer.user.color}` }}
              />,
            );
          }
        }
      }
    }
    return out;
  }, [peers, sheetId, rowIndexById, colIndexById, colEdges, rowEdges, inv]);

  // Editor overlay position — computed live so it follows layout shifts; the
  // last known position is kept if the target vanished (commit will toast).
  const lastEditorPosRef = useRef<{ left: number; top: number; width: number; minHeight: number } | null>(null);
  let editorPos: { left: number; top: number; width: number; minHeight: number } | null = null;
  if (editing !== null) {
    const r = rowIndexById.get(editing.rowId);
    const c = colIndexById.get(editing.colId);
    if (r !== undefined && c !== undefined) {
      const left = colEdges[c];
      const width = colWidths[c];
      const top = rowEdges[r];
      const bottom = rowEdges[r + 1];
      if (left !== undefined && width !== undefined && top !== undefined) {
        editorPos = {
          left,
          top,
          width,
          minHeight: bottom !== undefined ? bottom - top : DEFAULT_ROW_HEIGHT,
        };
        lastEditorPosRef.current = editorPos;
      }
    }
    if (editorPos === null) editorPos = lastEditorPosRef.current;
  }

  /** The editor box, shared verbatim by the textarea and its F-17-marks
   *  mirror — the two layers only stay aligned while every metric matches. */
  const editorBox =
    editorPos === null
      ? null
      : {
          left: editorPos.left,
          top: editorPos.top,
          width: editorPos.width,
          minHeight: editorPos.minHeight,
          fontFamily: TABLE_FONT_FAMILY,
          fontSize: TABLE_FONT_PX,
          lineHeight: TABLE_LINE_HEIGHT,
          padding: `${CELL_V_PAD - 1}px ${CELL_H_PAD - 1}px`,
        };

  /**
   * F-17-marks: where the cell editor's format bar goes. ABOVE the editor,
   * like the script view's selection menu — it covers nothing there, because
   * what the writer is reading is the text below it. It flips underneath only
   * when the editor sits too close to the top of the sheet for it to fit.
   */
  const fmtBarBelow = editorPos !== null && editorPos.top < (FMT_BAR_H + FMT_BAR_GAP) * inv;
  const fmtBarTop =
    editorPos === null
      ? 0
      : fmtBarBelow
        ? editorPos.top + Math.max(editorHeight, editorPos.minHeight) + FMT_BAR_GAP * inv
        : editorPos.top - FMT_BAR_GAP * inv;

  const gridTemplateColumns = colWidths.map((w) => `${w}px`).join(" ");

  // F-12b (amended 2026-07-15): the pill carries the COLUMN'S NAME (there is
  // no header row — F-10) and a "+" on BOTH sides. `locked` drives the F-28
  // affordances (no reorder cue, greyed delete in the menu).
  const hoverPill =
    hover !== null && canEdit
      ? hover.kind === "col"
        ? (() => {
            const left = colEdges[hover.index];
            const right = colEdges[hover.index + 1];
            const info = colInfos[hover.index];
            if (left === undefined || right === undefined || info === undefined) return null;
            return {
              kind: "col" as const,
              id: info.id,
              index: hover.index,
              // F-12b: the column's name; blank for an unnamed column (the
              // pill then shows only its grip — Miro-style, no invented "A").
              name: info.name,
              locked: info.locked,
              x: (left + right) / 2,
              y: -gutter / 2,
              // The pill must not outgrow its column: a long name ellipsizes.
              maxWidth: right - left,
            };
          })()
        : (() => {
            const top = rowEdges[hover.index];
            const bottom = rowEdges[hover.index + 1];
            const id = rowIds[hover.index];
            if (top === undefined || bottom === undefined || id === undefined) return null;
            // Rows have no name (F-23 is column metadata) — the row pill keeps
            // its grip + menu and gains the same both-sides "+" (top/bottom).
            return {
              kind: "row" as const,
              id,
              index: hover.index,
              name: "",
              locked: false,
              x: -gutter / 2,
              y: (top + bottom) / 2,
              maxWidth: undefined,
            };
          })()
      : null;

  /**
   * F-12b/F-13 (2026-07-15): the pill's "+" on either side is now the PRIMARY
   * add affordance (the persistent right/bottom edge strips are removed). The
   * neighbor's STABLE id is re-resolved to its CURRENT index at click time, so
   * a remote insert/delete/move between hover and click can't shift where the
   * new row/column lands — the same C4-style guard as the gutter bubbles.
   */
  const insertBesidePill = (
    pill: { kind: "col" | "row"; id: string },
    side: "before" | "after",
  ) => {
    if (pill.kind === "col") {
      const index = board.sortedColIds(sheetId).indexOf(pill.id);
      if (index < 0) return; // column deleted between hover and click
      const at = side === "before" ? index : index + 1;
      // F-28: re-checked at CLICK time (the pill hides the left "+" on the
      // source column, but a remote reorder could land between hover and click).
      if (!canInsertColumnBefore(at, sourceColIndex)) return;
      session.insertColumn(sheetId, at);
    } else {
      const index = board.sortedRowIds(sheetId).indexOf(pill.id);
      if (index < 0) return;
      session.insertRow(sheetId, side === "before" ? index : index + 1);
    }
  };

  const openHandleMenu = (clientX: number, clientY: number, kind: "col" | "row", index: number) => {
    setMenu({
      x: clientX,
      y: clientY,
      items: kind === "col" ? columnMenuItems(index) : rowMenuItems(index),
    });
  };

  return (
    <div
      ref={wrapRef}
      className={`grid-table-wrap${isSelecting ? " selecting" : ""}`}
      data-grid-table="true"
      // F-10: the wrapper carries the table's FULL extent. It used to
      // shrink-wrap its in-flow `.grid-table` child, but the windowed block is
      // out of flow and only as tall as the mounted rows — the camera must pan
      // over the whole table, and every board-space hit-test here is
      // wrapper-relative.
      style={{ left: GRID_ORIGIN.x, top: GRID_ORIGIN.y, width: tableW, height: tableH }}
      tabIndex={0}
      onPointerDownCapture={onWrapperPointerDownCapture}
      onPointerUpCapture={recordSelectPointer}
      onPointerDown={onWrapperPointerDown}
      onKeyDownCapture={forgetSelectPointer}
      onKeyDown={onWrapperKeyDown}
      onMouseMove={onWrapperMouseMove}
      onMouseLeave={() => {
        setHover(null);
        setAddHint(null);
      }}
    >
      {/* invisible hover gutters — F-12b handle zones above/left of the table */}
      <div
        className="gt-gutter"
        style={{ left: 0, top: -gutter, width: tableW, height: gutter }}
      />
      <div
        className="gt-gutter"
        style={{ left: -gutter, top: 0, width: gutter, height: tableH }}
      />

      {/* The table's frame — border, surface and shadow across the FULL
          extent. The mounted block can no longer provide it: it is out of flow
          and covers only the rows in view (F-10). */}
      <div className="grid-table-box" style={{ width: tableW, height: tableH }} />

      <div
        ref={tableRef}
        className="grid-table"
        // Absolutely positioned at the first mounted row's PREDICTED edge, just
        // inside the frame's border (colEdges[0] === rowEdges[0] === the border
        // width). The rows above and below occupy their extent with no nodes.
        style={{ gridTemplateColumns, left: TABLE_BORDER, top: blockTop }}
        onPointerDown={onTablePointerDown}
        onPointerMove={onTablePointerMove}
        onPointerUp={onTablePointerUp}
        onPointerCancel={onTablePointerUp}
        onDoubleClick={onTableDoubleClick}
        onContextMenu={onTableContextMenu}
      >
        {cells}
      </div>

      {/* presence (F-41/F-42) */}
      {peerOverlays}

      {/* local selection (F-15) */}
      {selectionRect !== null && (
        <div
          className="gt-selection"
          style={{
            left: selectionRect.left,
            top: selectionRect.top,
            width: selectionRect.width,
            height: selectionRect.height,
            borderWidth: 2 * inv,
            background: selectionRect.multi ? "rgba(79, 107, 237, 0.08)" : undefined,
          }}
        />
      )}

      {/* Touch range-select handles (coarse pointers only — CSS): with a
          one-finger table drag panning the board, dragging these corner dots
          is how touch users extend the selection (Sheets mobile). */}
      {selectionRect !== null &&
        canEdit &&
        !isEditing &&
        (
          [
            ["tl", selectionRect.left, selectionRect.top],
            ["br", selectionRect.left + selectionRect.width, selectionRect.top + selectionRect.height],
          ] as const
        ).map(([corner, cx, cy]) => (
          <div
            key={`sel-handle-${corner}`}
            className={`gt-sel-handle gt-sel-handle-${corner}`}
            style={{
              left: cx - 11 * inv,
              top: cy - 11 * inv,
              width: 22 * inv,
              height: 22 * inv,
              borderWidth: 2 * inv,
            }}
            onPointerDown={(e) => beginSelHandleDrag(e, corner)}
            onPointerMove={onSelHandleMove}
            onPointerUp={endSelHandleDrag}
            onPointerCancel={endSelHandleDrag}
          />
        ))}

      {/* F-14 boundary resize strips (col right edges / row bottom edges).
          Moves/ups ride window listeners (survive mid-drag unmounts);
          double-click = autofit to content. */}
      {canEdit &&
        colIds.map((colId, i) => {
          const edge = colEdges[i + 1];
          const width = colWidths[i];
          if (edge === undefined || width === undefined) return null;
          return (
            <div
              key={`crs-${colId}`}
              className="gt-resize gt-resize-col"
              style={{
                left: edge - (RESIZE_STRIP_SCREEN_PX / 2) * inv,
                top: 0,
                width: RESIZE_STRIP_SCREEN_PX * inv,
                height: tableH,
              }}
              onPointerDown={(e) => beginResize(e, "col", colId, width)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                autofitColumns(resizeGroupIds("col", colId));
              }}
            />
          );
        })}
      {/* F-10: strips only for the MOUNTED rows — one node per row was a third
          of the old DOM (3.3 nodes/row, not 2), and an unmounted row's
          boundary is off-screen, so there is nothing to grab. */}
      {canEdit &&
        !windowEmpty &&
        rowIds.slice(firstRow, lastRow + 1).map((rowId, i) => {
          const edge = rowEdges[firstRow + i + 1];
          const top = rowEdges[firstRow + i];
          if (edge === undefined || top === undefined) return null;
          return (
            <div
              key={`rrs-${rowId}`}
              className="gt-resize gt-resize-row"
              style={{
                left: 0,
                top: edge - (RESIZE_STRIP_SCREEN_PX / 2) * inv,
                width: tableW,
                height: RESIZE_STRIP_SCREEN_PX * inv,
              }}
              onPointerDown={(e) => beginResize(e, "row", rowId, edge - top)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                autofitRows(resizeGroupIds("row", rowId));
              }}
            />
          );
        })}

      {/* F-12b hover pill handle: name + grip + ⋮ menu, flanked by a "+" on
          BOTH sides (insert before / after — the primary add affordance since
          2026-07-15). The flanking buttons sit OUTSIDE the pill body so the
          body keeps owning the drag/click, and they counter-scale like it. */}
      {hoverPill !== null && (
        <div
          className={`gt-pill-group gt-pill-group-${hoverPill.kind}`}
          style={{
            left: hoverPill.x,
            top: hoverPill.y,
            gap: 3 * inv,
            transform: `translate(-50%, -50%) scale(${inv})`,
          }}
        >
          {/* F-28: no left "+" on the locked source column — nothing may be
              inserted to its left, so the affordance is withheld rather than
              silently redirected to its right. The right "+" stays. */}
          {(hoverPill.kind === "row" || canInsertColumnBefore(hoverPill.index, sourceColIndex)) && (
            <button
              type="button"
              className="gt-pill-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                insertBesidePill(hoverPill, "before");
              }}
              data-tip={hoverPill.kind === "col" ? "Insert column left" : "Insert row above"}
              aria-label={hoverPill.kind === "col" ? "Insert column left" : "Insert row above"}
            >
              +
            </button>
          )}
          <div
            className={`gt-pill gt-pill-${hoverPill.kind}${hoverPill.locked ? " locked" : ""}`}
            onPointerDown={(e) => beginPillDrag(e, hoverPill.kind, hoverPill.id, hoverPill.index)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openHandleMenu(e.clientX, e.clientY, hoverPill.kind, hoverPill.index);
            }}
            title={
              hoverPill.locked
                ? // F-28: the cue for "this one can't be reordered" — stated
                  // here and reinforced by the not-allowed grab cursor.
                  "Original Script is the translation source — it stays first and can't be renamed or deleted · click to select column"
                : hoverPill.kind === "col"
                  ? "Drag to reorder · click to select column"
                  : "Drag to reorder · click to select row"
            }
          >
            <span className="gt-pill-grip">⠿</span>
            {hoverPill.kind === "col" && hoverPill.name !== "" && (
              <span
                className="gt-pill-name"
                style={{ maxWidth: (hoverPill.maxWidth ?? 0) * zoom }}
              >
                {hoverPill.name}
              </span>
            )}
            {hoverPill.locked && (
              <span className="gt-pill-lock" aria-hidden="true">
                🔒
              </span>
            )}
            <button
              type="button"
              className="gt-pill-menu"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                openHandleMenu(e.clientX, e.clientY, hoverPill.kind, hoverPill.index);
              }}
              data-tip="Row/column menu"
              aria-label="Row/column menu"
            >
              ⋮
            </button>
          </div>
          <button
            type="button"
            className="gt-pill-add"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              insertBesidePill(hoverPill, "after");
            }}
            data-tip={hoverPill.kind === "col" ? "Insert column right" : "Insert row below"}
            aria-label={hoverPill.kind === "col" ? "Insert column right" : "Insert row below"}
          >
            +
          </button>
        </div>
      )}

      {/* F-12b reorder drop indicator */}
      {dragIndicator !== null &&
        (() => {
          const edges = dragIndicator.kind === "col" ? colEdges : rowEdges;
          const pos = edges[dragIndicator.boundary];
          if (pos === undefined) return null;
          return dragIndicator.kind === "col" ? (
            <div
              className="gt-drop-line"
              style={{ left: pos - inv, top: -gutter / 2, width: 2 * inv, height: tableH + gutter / 2 }}
            />
          ) : (
            <div
              className="gt-drop-line"
              style={{ left: -gutter / 2, top: pos - inv, width: tableW + gutter / 2, height: 2 * inv }}
            />
          );
        })()}

      {/* F-13: hover "+" bubble at a gutter boundary */}
      {canEdit && addHint !== null && (
        <button
          type="button"
          className="grid-add-bubble"
          style={{
            left: addHint.x,
            top: addHint.y,
            width: 20 * inv,
            height: 20 * inv,
            marginLeft: -10 * inv,
            marginTop: -10 * inv,
            fontSize: 14 * inv,
            lineHeight: `${20 * inv}px`,
          }}
          onClick={applyAddHint}
          data-tip={addHint.kind === "col" ? "Insert column here" : "Insert row here"}
          aria-label={addHint.kind === "col" ? "Insert column here" : "Insert row here"}
        >
          +
        </button>
      )}

      {/* F-13 (2026-07-15): the persistent right/bottom edge "+" strips are
          REMOVED — canvas clutter, redundant with the pill's both-sides "+"
          (F-12b), which is now the primary add affordance. Appending at the
          END stays reachable via the last row/column's pill "+" (after), the
          gutter boundary bubbles, and the context menus. */}

      {/* F-12 overlay editor — grows while typing, never clips (F-26).
          F-17-marks / F-17-type: for a cell that carries marks or line kinds
          it becomes TWO layers, the way a script block already is — a mirror painting the formatted runs
          and a transparent textarea over it holding the caret and the
          selection, because per-range bold/ink/highlight cannot exist inside a
          textarea. Every metric is shared (`editorBox`), which is what keeps
          the caret, the native selection and the wrap points aligned. */}
      {editing !== null && editorBox !== null && editorRanges !== undefined && (
        <div
          className="gt-editor-render"
          aria-hidden
          style={{ ...editorBox, height: editorHeight > 0 ? editorHeight : undefined }}
        >
          {segmentMarkedText(editing.value, editorRanges.marks, editorRanges.types).map(
            (segment, i) => (
              <span
                key={i}
                className={markClassName(segment)}
                style={markStyle(segment, themeColors.surface, themeColors.text)}
              >
                {segment.text}
              </span>
            ),
          )}
          {/* Gives a trailing newline a line to be on — without it the mirror
              is one row shorter than the textarea it is standing in for. */}
          {"\u200b"}
        </div>
      )}
      {editing !== null && editorBox !== null && (
        <textarea
          ref={editorRef}
          className={`gt-editor${editorRanges !== undefined ? " marked" : ""}`}
          style={editorBox}
          value={editing.value}
          onChange={(e) => {
            const { value, selectionStart } = e.target;
            setEditing((prev) => (prev === null ? prev : { ...prev, value }));
            setEditorCaret(selectionStart);
          }}
          onCompositionStart={() => {
            editorComposing.current = true;
          }}
          onCompositionEnd={() => {
            editorComposing.current = false;
            compositionEndedAt.current = performance.now();
          }}
          onSelect={(e) => {
            // An IME highlighting the clause it is converting is not a
            // selection the writer made — floating the format bar over a
            // half-typed word would be the same mistake as committing the cell
            // on its Enter.
            if (editorComposing.current) return;
            const el = e.currentTarget;
            setEditorSel(orderedRange(el.selectionStart, el.selectionEnd));
            // Caret moves without edits (arrows, clicks) open and close the
            // `/` menu too — the same rule the comment composer follows.
            setEditorCaret(el.selectionStart);
          }}
          onKeyDown={onEditorKeyDown}
          onBlur={() => {
            // Sheets commits when focus leaves the editor (Esc already closed).
            if (editingRef.current !== null) commitEditor();
          }}
          spellCheck={false}
        />
      )}

      {/* F-17-type: the `/` menu. Anchored BELOW the editor, opposite the
          format bar above it — the two can be open at once (a writer can have
          a selection and then type `/`), and stacking them on the same edge
          would put one on top of the other. */}
      {editing !== null && editorPos !== null && typeMenuOpen && (
        <ul
          className="gt-type-menu"
          role="listbox"
          aria-label="Line type"
          style={{
            left: editorPos.left,
            top: editorPos.top + Math.max(editorHeight, editorPos.minHeight) + FMT_BAR_GAP * inv,
            transformOrigin: "0 0",
            transform: `scale(${inv})`,
          }}
        >
          {typeMatches.map((option, i) => (
            <li key={option.label} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={i === typeMenuIndex}
                className={`gt-type-option${i === typeMenuIndex ? " active" : ""}`}
                // Pointer-DOWN, not click: a click blurs the textarea first,
                // and blur COMMITS the cell here — the pick would land after
                // the editor had already closed.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pickTextType(option);
                }}
                onMouseEnter={() => setTypeMenuActive(i)}
              >
                <span className={`gt-type-swatch ${textTypeSwatch(option).className}`}>
                  {textTypeSwatch(option).glyph}
                </span>
                <span className="gt-type-label">{option.label}</span>
                <span className="gt-type-hint">{option.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* F-17-marks: the cell editor's own format bar. F-27's toolbar hides
          while editing — it acts on WHOLE cells, and the writer is inside one —
          so a character range needs its own. Every control acts on pointerdown
          with preventDefault (ui/TextFormatControls.tsx), which is what keeps
          the selection it is about to format from being dropped by the focus
          move a click would cause; the press is stopped here so the wrapper
          does not read it as a click on empty canvas. */}
      {editing !== null && editorPos !== null && editorFormat !== null && !frozen && (
        <div
          className="gt-fmt-bar"
          role="menu"
          aria-label="Text formatting"
          style={{
            left: editorPos.left,
            top: fmtBarTop,
            // Anchored by its own BOTTOM-left when it sits above, so its
            // height never has to be known exactly (see FMT_BAR_H).
            transformOrigin: "0 0",
            transform: fmtBarBelow ? `scale(${inv})` : `scale(${inv}) translateY(-100%)`,
          }}
          onPointerDown={(event) => {
            // Keep focus — and therefore the selection this bar is about to
            // format — in the textarea, even for a press that lands on the
            // bar's own padding rather than on a button; and keep it from
            // reaching the wrapper, which would read it as a click on empty
            // canvas and drop the cell selection.
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <TextFormatControls
            format={{
              ...editorFormat,
              onToggle: toggleEditorMark,
              onColor: setEditorMarkColor,
            }}
          />
        </div>
      )}

      {menu !== null && <ContextMenu menu={menu} onClose={() => setMenu(null)} />}

      {/* F-23/F-12b column rename. Never opened for the locked source column
          (F-28 — the menu omits the item); `setColumnName` refuses it anyway. */}
      {renaming !== null && (
        <RenameColumnDialog
          initial={board.getColumn(sheetId, renaming)?.name ?? ""}
          onClose={() => setRenaming(null)}
          onSubmit={(name) => {
            setRenaming(null);
            // Re-check at SUBMIT time: the column may have been deleted while
            // the dialog sat open (C4) — the accessor no-ops, we just report.
            if (!session.setColumnName(sheetId, renaming, name)) {
              showToast("That column can't be renamed");
            }
          }}
        />
      )}
    </div>
  );
}

/** F-23 column rename dialog (F-12b: the name the hover pill shows). */
function RenameColumnDialog({
  initial,
  onSubmit,
  onClose,
}: {
  initial: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Modal title="Rename column" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value.trim());
        }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <input
          className="text-input"
          autoFocus
          placeholder="Column name"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          maxLength={200}
        />
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Rename
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Cell — memoized on primitives so remote edits repaint only damage   */
/* ------------------------------------------------------------------ */

interface CellViewProps {
  rowId: string;
  /** F-155: set on a "Place video" cell — the script path from the row above. */
  placeVideoScript?: string | undefined;
  colId: string;
  text: string;
  minHeight: number;
  /** F-17-bg cell fill (already normalized string | undefined via getCell). */
  bg: string | undefined;
  /**
   * F-17-color EXPLICIT text color, when the user picked one. Absent means
   * "automatic": the cell derives readable ink from `bg` + the theme below.
   */
  color: string | undefined;
  /** `--color-surface` / `--color-text` of the reader's theme (F-122). */
  surface: string;
  defaultText: string;
  claimColor: string | undefined;
  /** F-70 translation status: "mt" (machine-translated) / "outdated" (stale). */
  status: CellStatus | undefined;
  /** F-28: cell belongs to the locked "Original Script" column → base tint. */
  isSource: boolean;
  /** F-17-marks: per-range formatting of this cell's text; absent = none. */
  marks: CellTextMarks | undefined;
  /** F-17-type: line-kind switch points; absent = all narration, i.e. ordinary text. */
  types: readonly TextTypeSwitch[] | undefined;
}

/** F-70.5/.6 status tints: violet = machine-translated, amber = source changed. */
const STATUS_TINT: Record<CellStatus, string> = {
  mt: "rgba(79, 107, 237, 0.07)", // faint until a human edit clears `st`
  outdated: "rgba(217, 119, 6, 0.10)",
};

/**
 * F-28: the Original Script column's base tint — a faint warm ivory, chosen to
 * be distinct from the violet `mt` and amber `outdated` washes and from every
 * pastel F-17-bg preset, so the canonical source column reads at a glance.
 */
const SOURCE_COLUMN_TINT = "rgba(202, 138, 4, 0.07)";

const CellView = memo(function CellView({
  rowId,
  colId,
  text,
  minHeight,
  bg,
  color,
  surface,
  defaultText,
  claimColor,
  status,
  isSource,
  marks,
  types,
  placeVideoScript,
}: CellViewProps) {
  // F-17-bg paints UNDER everything else: the F-42 claim tint / F-70 status
  // wash layer over the fill as a translucent gradient; selection + presence
  // rects are separate translucent overlays above the cells, so they stay
  // readable on any fill. (CSV export stays values-only — fills are
  // presentation, features/import-export/csv.ts deliberately ignores them.)
  const tint =
    claimColor !== undefined
      ? withAlpha(claimColor, 0.14) // F-42 editing indicator tint
      : status !== undefined
        ? STATUS_TINT[status]
        : undefined;
  // F-122: the STORED fill is a light-mode pastel (board content, one value
  // for everyone); on a dark surface it is re-rendered into the same hue at a
  // dark lightness, so a highlighted cell reads as a tint rather than a slab
  // of near-white. A no-op in light mode (core/color.ts `themedFill`).
  const fill = themedFill(bg, surface);
  // F-28: the source tint is the BOTTOM layer — an explicit cell fill and any
  // claim/status wash paint over it, translucently, so both stay visible.
  const layers: string[] = [];
  if (tint !== undefined) layers.push(`linear-gradient(${tint}, ${tint})`);
  if (fill !== undefined) layers.push(`linear-gradient(${fill}, ${fill})`);
  if (isSource) layers.push(`linear-gradient(${SOURCE_COLUMN_TINT}, ${SOURCE_COLUMN_TINT})`);
  const background = layers.length > 0 ? layers.join(", ") : undefined;
  // F-17-color: an explicit color wins; otherwise a FILLED cell derives ink
  // that clears WCAG AA against its own fill (undefined = the theme's
  // `--color-text` already passes, so the CSS default stands). Derived from
  // the RENDERED fill, not the stored one — the ink has to be readable on
  // what is actually painted.
  const ink = color ?? readableTextColor(fill, surface, defaultText);
  // F-17-type: each sound/visual run is inked by its own segment's class
  // (ui/MarkedText.tsx) and drawn inside brackets. `tt-shown` — the italic,
  // the half that changes how wide the text is — is only ever worn here,
  // never by the editor's mirror layer.
  return (
    <div
      className={types === undefined ? "gt-cell" : "gt-cell tt-shown"}
      data-r={rowId}
      data-c={colId}
      data-st={status}
      style={{ minHeight, background, color: ink }}
    >
      {placeVideoScript !== undefined ? (
        <PlaceVideoButton scriptPath={placeVideoScript} />
      ) : (
        <MarkedText
          text={text}
          marks={marks}
          types={types}
          surface={surface}
          defaultText={defaultText}
        />
      )}
      {/* F-70.5 "small corner marker". The wash alone is too faint to notice on
          a filled cell (and invisible on a violet one); the marker is what
          actually says "a machine wrote this". Absolutely positioned, so it
          never enters the F-26 auto-grow scrollHeight measurement. Any human
          edit clears `st` (setCellValueAsHuman) and the marker with it. */}
      {status !== undefined && (
        <span
          className={`gt-cell-st gt-cell-st-${status}`}
          data-tip={
            status === "mt"
              ? "Machine-translated — edit the cell to mark it human-reviewed"
              : "The Original Script row changed after this was translated — translate the column again"
          }
        />
      )}
    </div>
  );
});
